import { useState, useMemo, useEffect } from "react";
import { ArrowDown, ArrowUp, ArrowUpDown, Filter, Search } from "lucide-react";
import DatePicker from "react-datepicker";
import { format } from "date-fns";
import "react-datepicker/dist/react-datepicker.css";
import DashboardLayout from "../../../components/dashboard-layout/DashboardLayout";
import SlaTimer from "../../../components/sla-timer/SlaTimer";
import styles from "../../../styles/__labOrderManagement.module.scss";
import ServiceNameTooltip from "../../../components/service-name-tooltip/ServiceNameTooltip";
import logo from "../../../assets/images/logo.png";
import user from "../../../assets/images/user.png";
import defaultAvatar from "../../../assets/images/default_avatar.png";
import { IoMdSettings } from "react-icons/io";
import { PiChatCircleTextLight } from "react-icons/pi";
import DialogReschedule from "../../../components/dialog-reschedule/DialogReschedule";
import DialogCreateFollowUp from "../../../components/dialog-create/DialogCreateFollowUp";
import DialogManageAvailability from "../../../components/dialog-manage-availability/DialogManageAvailability";
import DialogPatientRegistration from "../../../components/patient-registration/DialogPatientRegistration";
import Chat from "../../../components/chat/ChatModal";
import ChatIconWithBadge from "../../../components/chat/ChatIconWithBadge";
import useChatUnread from "../../../hooks/useChatUnread";
import { useTranslation } from "react-i18next";
import {
  BookingPatientNameCell,
  BookingChildNameCell,
} from "../../../components/booking-patient-cells/BookingPatientCells";
import { useDispatch, useSelector } from "react-redux";
import { joinDoctorAppointmentReducer } from "../../../reducer/joinDoctorAppointment/joinDoctorAppointmentReducer";
import AppointmentDetails from "../../../components/appointmentDetails/AppointmentDetails";
import Loader from "../../../components/loader/Loader";
import LabDashboardLayout from "../../../components/labmod-dashboard-layout/LabModDashboardLayout";
import { FiDownload, FiPrinter } from "react-icons/fi";
import { Link, useNavigate } from "react-router-dom";
import { fetchLabOrders } from "../../../slice/labOrders/labOrdersSlice";
import LabInvoiceModal from "../../../components/lab-invoice-modal/LabInvoiceModal";
import { toast } from "../../../components/toast";
import { APP_PATH } from "../../../api/backendPath";
import { formatDateNumeric as tzDateNumeric } from "../../../utils/datetime";

// Formats an amount with thousand separators (e.g. 39999 -> "39,999").
// Accepts numbers, numeric strings, or already-comma-formatted strings and
// falls back to "0" for null/undefined/NaN so the table never shows "NaN"
// when a row is missing its total.
const formatAmount = (value) => {
  if (value === null || value === undefined || value === "") return "0";
  const num = parseFloat(String(value).replace(/,/g, ""));
  return isNaN(num) ? "0" : Math.round(num).toLocaleString("en-US");
};

const LabOrderManagement = ({ onLogout, onNavigateToDashboard }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [showDetails, setShowDetails] = useState(false);
  const [activeTab, setActiveTab] = useState("Received");
  const [currentPage, setCurrentPage] = useState(1);
  // Sort state.
  // `sortField === "default"` is the app's baseline: SLA-overdue orders float
  // to the top (so operators see anything already breached first) and the rest
  // are ordered by request date, newest first. Clicking the Request Date
  // header switches into an explicit `requestDateRaw` sort (asc → desc → back
  // to default on subsequent clicks) so the operator can toggle chronological
  // order when they want to.
  const [sortField, setSortField] = useState("default");
  const [sortDirection, setSortDirection] = useState("desc");
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("Received"); // Default to "Received" tab status
  const [testTypeFilter, setTestTypeFilter] = useState("All");
  const [dateFilter, setDateFilter] = useState("");
  // Location filter (replaces the old status dropdown): "", HOME_VISIT, ON_SITE
  const [locationFilter, setLocationFilter] = useState("");
  // Search typeahead dropdown state
  const [searchResults, setSearchResults] = useState([]);
  const [showSearchDropdown, setShowSearchDropdown] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  // Invoice (Print) modal state
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [invoiceOrder, setInvoiceOrder] = useState(null);
  const [centerProfile, setCenterProfile] = useState(null);
  const PRINTABLE_STATUSES = [
    "ORDER_ACCEPTED",
    "TECHNICIAN_ASSIGNED",
    "TECHNICIAN_DISPATCHED",
    "IN_PROGRESS",
    "COMPLETED",
  ];
  const itemsPerPage = 10;
  const [date, setDate] = useState(null);
  const [openDialogReschedule, setOpenDialogReschedule] = useState(false);
  const [openDialogCreateFollowUp, setOpenDialogCreateFollowUp] =
    useState(false);
  const [openDialogManageAvailability, setOpenDialogManageAvailability] =
    useState(false);
  const [openDialogPatientRegistration, setOpenDialogPatientRegistration] =
    useState(false);
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [chatTarget, setChatTarget] = useState(null);
  const {
    unreadForAppointment,
    refresh: refreshChatUnread,
    clearAppointment,
  } = useChatUnread();

  const openChat = (item) => {
    setChatTarget(item);
    setIsChatOpen(true);
    clearAppointment(item?.raw?.id ?? item?.raw?.booking_id ?? item?.id);
  };

  const closeChat = () => {
    setIsChatOpen(false);
    setChatTarget(null);
    refreshChatUnread();
  };

  const dispatch = useDispatch();

  // Use lab orders from Redux store
  const {
    data: labOrders,
    isLoading: loading,
    error,
    totalPages: reduxTotalPages,
  } = useSelector((state) => state.labOrders);

  // Export functionality
  const handleExport = async () => {
    try {
      const token = sessionStorage.getItem("usr_1a2b3c");
      if (!token) {
        toast.error("Authentication required. Please login again.");
        return;
      }

      // Build query parameters based on current filters
      const params = new URLSearchParams();

      // Map activeTab to status. `RESULTS_PENDING_OR_SENT` is a pseudo-status
      // the backend recognises — filters on booking.status = COMPLETED AND
      // booking.payment_status = COMPLETED so the tab surfaces every fully-
      // paid completed booking whether its TEST_RESULT document has been
      // uploaded yet or not (see `getLabOrders` in lab-order.service.ts).
      const statusMapping = {
        Received: "REQUEST_SUBMITTED",
        "Awaiting Confirmation": "AWAITING_CONFIRMATION",
        "Awaiting Payment": "AWAITING_PAYMENT",
        "Order Accepted": "ORDER_ACCEPTED",
        Scheduled: "TECHNICIAN_ASSIGNED",
        Dispatched: "TECHNICIAN_DISPATCHED",
        "In-Progress": "IN_PROGRESS",
        Completed: "COMPLETED",
        "Results / Pending Results": "RESULTS_PENDING_OR_SENT",
        Rejected: "REJECTED",
      };

      if (activeTab && statusMapping[activeTab]) {
        params.append("status", statusMapping[activeTab]);
      }

      if (dateFilter) {
        params.append("date", dateFilter);
      }

      if (searchTerm) {
        params.append("search", searchTerm);
      }

      const baseURL = process.env.REACT_APP_API_URL || "";
      const response = await fetch(
        `${baseURL}${APP_PATH.exportLabOrders}?${params.toString()}`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      // Check if response is CSV (our backend returns CSV directly)
      const contentType = response.headers.get("content-type");
      if (contentType && contentType.includes("text/csv")) {
        // Handle direct CSV response
        const csvContent = await response.text();
        const blob = new Blob([csvContent], { type: "text/csv" });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = "lab-orders.csv";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);

        toast.success("Data exported successfully!");
      } else {
        // Handle JSON response (fallback for other implementations)
        const result = await response.json();

        if (result.status && result.data) {
          // Create and download CSV file
          const blob = new Blob([result.data.csv || result.data], {
            type: "text/csv",
          });
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement("a");
          link.href = url;
          link.download = result.data.filename || "lab-orders.csv";
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          window.URL.revokeObjectURL(url);

          toast.success("Data exported successfully!");
        } else {
          throw new Error(result.message || "Export failed");
        }
      }
    } catch (error) {
      console.error("Export error:", error);
      toast.error(`Failed to export data: ${error.message}`);
    }
  };

  // Use Redux state for totalPages, fallback to local state if needed
  const totalPages = reduxTotalPages || 1;
  const today = new Date();
  const formatted = format(today, "MMMM dd, yyyy");
  const [selectedAppointment, setSelectedAppointment] = useState(null);

  // Component-level 12-hour time formatter for cells that render the RAW order
  // (item.raw.serviceTime) instead of the mapped/formatted item.serviceTime —
  // e.g. the Completed and Rejected tabs. Passes through values already carrying
  // AM/PM and returns "N/A" for empties.
  const formatTime12h = (timeStr) => {
    if (!timeStr) return "N/A";
    const s = String(timeStr);
    if (s.includes("AM") || s.includes("PM")) return s;
    const [h, m] = s.split(":");
    const hour = parseInt(h, 10);
    if (Number.isNaN(hour)) return s;
    const ampm = hour >= 12 ? "PM" : "AM";
    const displayHour = hour % 12 || 12;
    return `${displayHour}:${String(m ?? "00").padStart(2, "0")} ${ampm}`;
  };

  // Map lab orders from API to display format
  const appointments = (labOrders || []).map((order) => {
    const patient = order?.patient || {};
    // Guardian (account owner) full name — always computed as a fallback and
    // as the value we show for self bookings.
    const guardianName =
      `${patient?.full_name || ""} ${
        patient?.surname || ""
      }`.trim();
    // Child bookings: the clinical patient IS the child, so the Name column
    // surfaces the child's snapshotted name. Falls back to the guardian name
    // when the child snapshot is somehow missing.
    const isChildFlow =
      order?.is_for_child === true || order?.isForChild === true;
    // Child's full name: given + family, so the row reads like an adult patient's.
    const childName =
      [order?.child_name || order?.childName, order?.child_surname]
        .filter(Boolean)
        .join(" ") || null;
    const name =
      (isChildFlow && childName ? childName : guardianName) || "N/A";
    const avatar = patient?.avatar_presigned_url || defaultAvatar;

    // Extract test names from tests array. Lab test names are English-only.
    const testName =
      order?.tests?.length > 0
        ? order.tests.map((test) => test.test_name).join(", ")
        : order.testName || "N/A";

    // Format dates
    const formatDate = (dateStr) => {
      if (!dateStr) return "N/A";
      try {
        const date = new Date(dateStr);
        return tzDateNumeric(date); // DD/MM/YYYY format
      } catch {
        return dateStr;
      }
    };

    // Format time
    const formatTime = (timeStr) => {
      if (!timeStr) return "N/A";
      if (timeStr.includes("AM") || timeStr.includes("PM")) return timeStr;
      try {
        const [hours, minutes] = timeStr.split(":");
        const hour = parseInt(hours);
        const ampm = hour >= 12 ? "PM" : "AM";
        const displayHour = hour % 12 || 12;
        return `${displayHour}:${minutes} ${ampm}`;
      } catch {
        return timeStr;
      }
    };

    return {
      id: order.id, // Use numeric ID for API calls
      bookingNumber: order.bookingNumber || order.booking_number,
      name,
      avatar,
      // The two people on a child booking, split the way the doctor app splits a child
      // consultation: Patient Name is the ADULT account holder ("No Patient" on a child
      // booking) and the child gets its own column. `name` above is left as-is because the
      // search dropdown and the chat title key off it. `childName` is empty for a self booking.
      guardianName: guardianName || "N/A",
      childName: isChildFlow ? childName || "" : "",
      requestDate: formatDate(order.requestDate || order.created_at),
      // Raw ISO timestamp (unformatted) used for chronological sorting by
      // request date — the display value above is a DD/MM/YYYY string and can't
      // be compared numerically.
      requestDateRaw: order.requestDate || order.created_at || null,
      preferredDate: formatDate(order.preferredDate || order.preferred_date),
      preferredTimeSlot:
        order.preferredTimeSlot || order.preferred_time_slot || "N/A",
      urgency: order.urgency === "Normal" ? "" : order.urgency || "",
      preferredGender:
        order.preferredGender || order.preferred_technician_gender || "",
      testType: order.testType || "Lab",
      testName,
      preferredLocation:
        order.preferredLocation || order.preferred_location || "N/A",
      serviceDate: formatDate(order.serviceDate || order.service_date),
      serviceTime: formatTime(order.serviceTime || order.service_time),
      // Show "Not Assigned" across every tab when no technician has been assigned yet.
      technician:
        order?.technician?.name || order?.technician_name || "Not Assigned",
      location:
        order.location ||
        order.preferredLocation ||
        order.preferred_location ||
        "N/A",
      totalPrice: order.totalPrice || order.total_price || order.price || "0",
      paymentStatus: order.paymentStatus || order.payment_status || "",
      sampleStatus: order.sampleStatus || order.sample_status || "",
      technicianStatus: order.technicianStatus || order.technician_status || "",
      reportStatus: order.reportStatus || order.report_status || "",
      status: order.status || "",
      // SLA fields for the order-management SLA column (center-action + patient-confirmation).
      slaDueAt: order.slaDueAt || order.sla_due_at || null,
      slaIsUrgent: order.slaIsUrgent ?? order.sla_is_urgent ?? false,
      patientConfirmDueAt:
        order.patientConfirmDueAt || order.patient_confirm_due_at || null,
      // Override raw.testName with the language-aware value so tables that read
      // item.raw.testName (Completed/Dispatched views) also show Arabic names.
      raw: { ...order, testName },
    };
  });

  // Fields that store raw ISO timestamps and must be compared as dates rather
  // than as strings (string comparison on ISO works only when both values are
  // well-formed and non-null — parse to epoch for safety).
  const DATE_SORT_FIELDS = ["requestDateRaw"];

  // Convert an ISO timestamp to epoch ms, tolerating null / bad input.
  const toEpoch = (value) => {
    if (!value) return 0;
    const t = new Date(value).getTime();
    return Number.isNaN(t) ? 0 : t;
  };

  // Sort appointments (filtering is done server-side).
  // Two modes:
  //   1. "default" — SLA-overdue rows first (most-overdue-first), everything
  //      else ordered by request date newest-first as a stable secondary key.
  //      This is what the operator sees on page load so anything breaching SLA
  //      is impossible to miss.
  //   2. Explicit column sort (currently only `requestDateRaw`) — pure
  //      chronological order in the direction the operator picked from the
  //      Request Date header arrow.
  const sortedAppointments = useMemo(() => {
    const isDateField = DATE_SORT_FIELDS.includes(sortField);
    const now = Date.now();

    const overdueMsFor = (item) => {
      const dueMs = toEpoch(item?.slaDueAt);
      if (!dueMs) return 0; // no SLA due-at → not overdue
      const past = now - dueMs;
      return past > 0 ? past : 0; // positive = ms overdue
    };

    const sorted = [...appointments].sort((a, b) => {
      if (sortField === "default") {
        // Overdue rows bubble to the top; among overdue rows, the row that
        // has been overdue the longest wins. Non-overdue rows fall through
        // to the request-date tiebreaker (newest first).
        const aOverdue = overdueMsFor(a);
        const bOverdue = overdueMsFor(b);
        if (aOverdue > 0 || bOverdue > 0) {
          if (aOverdue !== bOverdue) return bOverdue - aOverdue;
        }
        return toEpoch(b?.requestDateRaw) - toEpoch(a?.requestDateRaw);
      }

      const aValue = a[sortField];
      const bValue = b[sortField];

      if (isDateField) {
        const safeA = toEpoch(aValue);
        const safeB = toEpoch(bValue);
        return sortDirection === "asc" ? safeA - safeB : safeB - safeA;
      }

      if (aValue < bValue) return sortDirection === "asc" ? -1 : 1;
      if (aValue > bValue) return sortDirection === "asc" ? 1 : -1;
      return 0;
    });

    return sorted;
  }, [appointments, sortField, sortDirection]);

  // Pagination
  // const totalPages = Math.ceil(
  //   filteredAndSortedAppointments.length / itemsPerPage
  // );
  // const startIndex = (currentPage - 1) * itemsPerPage;
  // const paginatedAppointments = filteredAndSortedAppointments.slice(
  //   startIndex,
  //   startIndex + itemsPerPage
  // );

  const paginatedAppointments = sortedAppointments;

  // Three-state sort cycle so the operator can always get back to the
  // SLA-overdue-first default without reloading:
  //   default → asc  → desc  → default
  //
  // Sort is now applied server-side, so every cycle transition kicks off a
  // page-1 refetch with the NEW sort. We can't rely on `sortField` /
  // `sortDirection` state having been applied by the time we dispatch — React
  // batches those updates — so we compute the next values inline and pass
  // them into `fetchAppointmentsWithFilters` via the `sortOverride`
  // parameter. Resetting to page 1 is intentional: the globally-newest
  // (or oldest) 10 rows aren't necessarily the same 10 the user was on.
  const handleSort = (field) => {
    let nextField;
    let nextDirection;
    if (sortField !== field) {
      nextField = field;
      nextDirection = "asc";
    } else if (sortDirection === "asc") {
      nextField = field;
      nextDirection = "desc";
    } else {
      // Was desc → back to the SLA-overdue-first default.
      nextField = "default";
      nextDirection = "desc";
    }
    setSortField(nextField);
    setSortDirection(nextDirection);
    setCurrentPage(1);
    fetchAppointmentsWithFilters(
      1,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      { field: nextField, direction: nextDirection },
    );
  };

  // Renders a clickable table header that toggles asc / desc sorting on the
  // given field and shows a directional arrow (up / down / two-way).
  // Reused across every tab table that displays a Request Date column so the
  // sort control looks consistent everywhere.
  const renderSortableHeader = (field, label) => {
    const isActive = sortField === field;
    const ariaSort = isActive
      ? sortDirection === "asc"
        ? "ascending"
        : "descending"
      : "none";
    return (
      <th
        className={styles["sortable"]}
        onClick={() => handleSort(field)}
        role="button"
        tabIndex={0}
        aria-sort={ariaSort}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            handleSort(field);
          }
        }}
      >
        <span
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 4,
          }}
        >
          {label}
          {!isActive ? (
            <ArrowUpDown size={14} style={{ opacity: 0.4 }} />
          ) : sortDirection === "asc" ? (
            <ArrowUp size={14} />
          ) : (
            <ArrowDown size={14} />
          )}
        </span>
      </th>
    );
  };

  const formatDate = (dateObj) => {
    if (!dateObj) return null;
    try {
      const year = dateObj.getFullYear();
      const month = String(dateObj.getMonth() + 1).padStart(2, "0");
      const day = String(dateObj.getDate()).padStart(2, "0");
      return `${year}-${month}-${day}`;
    } catch (error) {
      console.error("Error formatting date:", error);
      return null;
    }
  };

  // Format payment status for display
  const formatPaymentStatus = (status) => {
    if (!status) return "-";

    const statusMap = {
      PENDING: "Pending",
      COMPLETED: "Completed",
      FAILED: "Failed",
      COD: "COD",
      CASH: "Cash",
      CARD: "Card Payment",
      WALLET: "Wallet Payment",
      INSURANCE: "Insurance",
    };

    return statusMap[status.toUpperCase()] || status;
  };

  // Format location for display
  const formatLocation = (location) => {
    if (!location) return "N/A";

    const locationMap = {
      HOME_VISIT: "Home Visit",
      VISIT_CENTER: "On-Site",
      ON_SITE: "On-Site",
      "VISIT CENTER": "On-Site",
      "HOME VISIT": "Home Visit",
    };

    return locationMap[location.toUpperCase()] || location;
  };

  // Translate a canonical English status/label for DISPLAY only. The original
  // English value is still used by getStatusColor for styling. Falls back to the
  // value itself when no translation key exists.
  const translateStatus = (value) => {
    if (value === null || value === undefined || value === "") return value;
    const keyMap = {
      Pending: "lab.orderManagement.status.pending",
      "Payment Complete": "lab.orderManagement.status.paymentComplete",
      Failed: "lab.orderManagement.status.failed",
      COD: "lab.orderManagement.status.cod",
      Cash: "lab.orderManagement.status.cash",
      "Card Payment": "lab.orderManagement.status.cardPayment",
      "Wallet Payment": "lab.orderManagement.status.walletPayment",
      Insurance: "lab.orderManagement.status.insurance",
      "Home Visit": "lab.orderManagement.status.homeVisit",
      "On-Site": "lab.orderManagement.status.onSite",
      "Visit Center": "lab.orderManagement.status.visitCenter",
      Lab: "lab.orderManagement.status.lab",
      Imaging: "lab.orderManagement.status.imaging",
      Urgent: "lab.orderManagement.status.urgent",
      "Not Urgent": "lab.orderManagement.status.notUrgent",
      Normal: "lab.orderManagement.status.normal",
      "Technician Dispatched":
        "lab.orderManagement.status.technicianDispatched",
      Sent: "lab.orderManagement.status.sent",
      "Not Sent": "lab.orderManagement.status.notSent",
      Collected: "lab.orderManagement.status.collected",
      Male: "lab.orderManagement.status.male",
      Female: "lab.orderManagement.status.female",
    };
    const key = keyMap[value];
    return key ? t(key, value) : value;
  };

  // Normalize status value for API (convert display format to API format)
  const normalizeStatusForAPI = (status) => {
    if (!status || status === "All") return null;

    // Map frontend tab names to backend BookingStatus enum values
    const statusMap = {
      // Tab names to backend enum
      Received: "REQUEST_SUBMITTED",
      "Awaiting Patient Confirmation": "AWAITING_CONFIRMATION",
      "Awaiting Payment": "INVOICE_RECEIVED", // Changed: Awaiting Payment now maps to INVOICE_RECEIVED
      "Order Accepted": "ORDER_ACCEPTED",
      Scheduled: "TECHNICIAN_ASSIGNED",
      Dispatched: "TECHNICIAN_DISPATCHED",
      "In-Progress": "IN_PROGRESS",
      Completed: "COMPLETED",
      "Results / Pending Results": "RESULTS_PENDING_OR_SENT",
      Rejected: "REJECTED",

      // Also handle lowercase versions
      received: "REQUEST_SUBMITTED",
      "awaiting patient confirmation": "AWAITING_CONFIRMATION",
      "awaiting payment": "INVOICE_RECEIVED", // Changed: lowercase version
      "order accepted": "ORDER_ACCEPTED",
      scheduled: "TECHNICIAN_ASSIGNED",
      dispatched: "TECHNICIAN_DISPATCHED",
      "in-progress": "IN_PROGRESS", // Changed: lowercase version
      completed: "COMPLETED",
      "results / pending results": "RESULTS_PENDING_OR_SENT",
      rejected: "REJECTED",

      // Handle backend enum values directly (in case they come from API)
      REQUEST_SUBMITTED: "REQUEST_SUBMITTED",
      AWAITING_CONFIRMATION: "AWAITING_CONFIRMATION",
      INVOICE_RECEIVED: "INVOICE_RECEIVED",
      AWAITING_PAYMENT: "AWAITING_PAYMENT",
      ORDER_ACCEPTED: "ORDER_ACCEPTED",
      TECHNICIAN_ASSIGNED: "TECHNICIAN_ASSIGNED",
      TECHNICIAN_DISPATCHED: "TECHNICIAN_DISPATCHED",
      IN_PROGRESS: "IN_PROGRESS",
      COMPLETED: "COMPLETED",
      // Pseudo-status handled by the backend (see `getLabOrders` in
      // lab-order.service.ts) — pass through unchanged when the caller
      // already sends the API-side name.
      RESULTS_PENDING_OR_SENT: "RESULTS_PENDING_OR_SENT",
      REJECTED: "REJECTED",
    };

    return statusMap[status] || status.toUpperCase().replace(/\s+/g, "_");
  };

  // Function to fetch appointments with current filters
  // Optional parameters allow overriding filter values (useful for tab clicks and reset)
  // Use undefined as default to distinguish between "not provided" and "explicitly null"
  const fetchAppointmentsWithFilters = (
    pageNum = currentPage,
    statusOverride = undefined,
    dateOverride = undefined,
    searchOverride = undefined,
    testTypeOverride = undefined,
    locationOverride = undefined,
    sortOverride = undefined,
  ) => {
    // Use override values if provided (including null), otherwise use current state
    // undefined means "use state value", null means "explicitly clear/no filter"
    const dateToUse = dateOverride !== undefined ? dateOverride : date;
    const statusToUse =
      statusOverride !== undefined ? statusOverride : statusFilter;
    const searchToUse =
      searchOverride !== undefined ? searchOverride : searchTerm;
    const testTypeToUse =
      testTypeOverride !== undefined ? testTypeOverride : testTypeFilter;
    const locationToUse =
      locationOverride !== undefined ? locationOverride : locationFilter;
    // Sort override is used by handleSort so the refetch fires with the
    // NEW sort state (React state updates are async — sortField / direction
    // may not have applied yet when the fetch is queued).
    const sortFieldToUse =
      sortOverride?.field !== undefined ? sortOverride.field : sortField;
    const sortDirectionToUse =
      sortOverride?.direction !== undefined
        ? sortOverride.direction
        : sortDirection;

    const formattedDate = formatDate(dateToUse);
    const normalizedStatus = normalizeStatusForAPI(statusToUse);

    // Build params object - only include defined values
    const params = {
      page: pageNum,
      limit: itemsPerPage,
    };

    // Only add date if it's a valid formatted date string (not null/undefined/empty)
    if (
      formattedDate &&
      formattedDate !== null &&
      formattedDate !== "null" &&
      formattedDate !== ""
    ) {
      params.date = formattedDate;
    }

    if (searchToUse && searchToUse.trim()) {
      params.search = searchToUse.trim();
    }

    if (normalizedStatus) {
      params.status = normalizedStatus;
    }

    if (testTypeToUse && testTypeToUse !== "All") {
      params.testType = testTypeToUse;
    }

    if (locationToUse) {
      params.location = locationToUse;
    }

    // Server-side sort — only send when an explicit column sort is active
    // (the "default" state means SLA-overdue first + newest-first, which the
    // backend applies as its default order when no sort params are given).
    if (sortFieldToUse && sortFieldToUse !== "default") {
      params.sortField = sortFieldToUse;
      params.sortDirection = sortDirectionToUse || "asc";
    }

    // Dispatch the fetchLabOrders action
    dispatch(fetchLabOrders(params));
  };

  const handlePageChange = (page) => {
    if (page < 1 || page > totalPages) return;
    fetchAppointmentsWithFilters(page);
  };

  const resetFilters = () => {
    // Reset state values first
    setSearchTerm("");
    setStatusFilter("Received"); // Reset to default "Received" tab
    setTestTypeFilter("All");
    setLocationFilter("");
    setDate(null);
    setSearchResults([]);
    setShowSearchDropdown(false);
    setCurrentPage(1);
    setActiveTab("Received"); // Reset to default "Received" tab

    // Immediately fetch with reset values (pass them directly to avoid async state update issue)
    // Explicitly pass null for date to ensure it's cleared
    fetchAppointmentsWithFilters(1, "Received", null, "", "All", "");
  };

  const handleNavigate = (page) => {
    if (page === "dashboard") {
      onNavigateToDashboard();
    }
  };

  // Debounced search typeahead — matches booking number OR patient name across
  // all statuses; results shown in a dropdown, clicking navigates to details.
  useEffect(() => {
    const term = searchTerm.trim();
    if (!term) {
      setSearchResults([]);
      setShowSearchDropdown(false);
      setSearchLoading(false);
      return;
    }
    const handler = setTimeout(async () => {
      try {
        setSearchLoading(true);
        const token = sessionStorage.getItem("usr_1a2b3c");
        const baseURL = process.env.REACT_APP_API_URL || "";
        const res = await fetch(
          `${baseURL}${APP_PATH.getLabOrders}?search=${encodeURIComponent(term)}&limit=10`,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        const json = await res.json();
        setSearchResults(json?.data?.orders || []);
        setShowSearchDropdown(true);
      } catch (e) {
        setSearchResults([]);
      } finally {
        setSearchLoading(false);
      }
    }, 300);
    return () => clearTimeout(handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm]);

  // Open the printable invoice — fetches full order details (invoice + patient + insurance).
  const openInvoice = async (item) => {
    try {
      const token = sessionStorage.getItem("usr_1a2b3c");
      const baseURL = process.env.REACT_APP_API_URL || "";
      const res = await fetch(`${baseURL}${APP_PATH.getLabOrders}/${item.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const json = await res.json();
      if (json?.data) {
        setInvoiceOrder(json.data);
        setShowInvoiceModal(true);
        // Load the center profile once for the invoice header (name + logo).
        if (!centerProfile) {
          fetch(`${baseURL}/api/v1/lab/profile`, {
            headers: { Authorization: `Bearer ${token}` },
          })
            .then((r) => r.json())
            .then((pj) => {
              if (pj?.data) setCenterProfile(pj.data);
            })
            .catch(() => {});
        }
      } else {
        toast.error(json?.message || "Failed to load invoice");
      }
    } catch (e) {
      toast.error("Failed to load invoice");
    }
  };

  // Initial load with default filters
  useEffect(() => {
    fetchAppointmentsWithFilters(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handlePageChange1 = (data) => {
    const nextPage = data + 1;
    fetchAppointmentsWithFilters(nextPage);
  };

  const handlePageChange11 = (data) => {
    const prevPage = data - 1;
    if (prevPage >= 1) {
      fetchAppointmentsWithFilters(prevPage);
    }
  };
  const handleBackDetail = (state, data) => {
    setShowDetails(state);
    setSelectedAppointment(data);
  };

  const handleReschedules = (data) => {
    setOpenDialogReschedule(true);
    setSelectedAppointment(data);
  };

  const CreateFollowUp = (data) => {
    setOpenDialogCreateFollowUp(true);
    setSelectedAppointment(data);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "Upcoming":
        return "status-upcoming";
      case "Completed":
        return "status-completed";
      case "Cancelled":
        return "status-cancelled";
      case "Reschedule Requested":
        return "status-reschedule";

      // ---- Payment status / method badges (each a distinct color) ----
      case "Pending":
      case "PENDING":
        return "status-pending"; // Orange
      case "Payment Complete":
      case "COMPLETED":
      case "Paid":
      case "PAID":
        return "status-payment"; // Green
      case "Failed":
      case "FAILED":
        return "status-red"; // Red
      case "COD":
        return "status-cod"; // Yellow
      case "Cash":
      case "CASH":
        return "status-dispatched"; // Teal
      case "Card Payment":
      case "CARD":
        return "status-homevisit-blue"; // Blue
      case "Wallet Payment":
      case "WALLET":
        return "status-labtype"; // Purple
      case "Insurance":
      case "INSURANCE":
        return "status-insurance"; // Indigo
      case "Collected":
        return "status-payment";
      case "Lab":
        return "status-labtype";
      case "Home Visit":
        return "status-homevisit-blue";
      case "Visit Center":
      case "On-Site":
        return "status-imagingtype";
      case "Imaging":
        return "status-imagingtype";
      case "Urgent":
        return "status-red";
      case "Technician Dispatched":
        return "status-dispatched";
      case "Sent":
        return "status-dispatched";
      case "MALE":
      case "Male":
        return "status-labtype";
      case "FEMALE":
      case "Female":
        return "status-imagingtype";
      default:
        return "";
    }
  };

  // Specific function for report status styling
  const getReportStatusColor = (reportStatus) => {
    switch (reportStatus) {
      case "SENT":
      case "Sent":
        return "reportstatus-Sent";
      case "NOT_SENT":
      case "Not Sent":
      case "Pending":
      default:
        return "reportstatus-NotSent";
    }
  };

  // Format report status for display
  const formatReportStatus = (reportStatus) => {
    switch (reportStatus) {
      case "SENT":
        return "Sent";
      case "NOT_SENT":
        return "Not Sent";
      case "Pending":
      case null:
      case undefined:
      default:
        return "Not Sent";
    }
  };

  const getTabTranslationKey = (tabName) => {
    const tabKeyMap = {
      Received: "received",
      "Awaiting Confirmation": "awaitingConfirmation",
      "Awaiting Payment": "awaitingPayment",
      "Order Accepted": "orderAccepted",
      Scheduled: "scheduled",
      Dispatched: "dispatched",
      "In-Progress": "inProgress",
      Completed: "completed",
      "Results / Pending Results": "resultsPendingOrSent",
      Rejected: "rejected",
    };
    return `lab.orderManagement.tabs.${tabKeyMap[tabName] || tabName.toLowerCase()}`;
  };

  const orderManagementTabs = [
    "Received", // Maps to REQUEST_SUBMITTED
    // "Awaiting Confirmation" tab hidden (split/confirmation flow retired). Status
    // mapping + render block are kept for backward compatibility with existing orders.
    "Awaiting Payment", // Maps to INVOICE_RECEIVED (invoice generated, awaiting payment)
    "Order Accepted", // Maps to ORDER_ACCEPTED
    "Scheduled", // Maps to TECHNICIAN_ASSIGNED
    "Dispatched", // Maps to TECHNICIAN_DISPATCHED
    "In-Progress", // Maps to IN_PROGRESS
    "Completed", // Maps to COMPLETED
    // Consolidated "Results / Pending Results" tab — surfaces every fully-paid,
    // completed booking regardless of whether the test-result document has
    // been uploaded / sent, so officers can spot orders still awaiting a
    // report AND review orders whose report has already gone out from the
    // same list. Backend pseudo-status `RESULTS_PENDING_OR_SENT` filters
    // `booking.status = COMPLETED AND booking.payment_status = COMPLETED`.
    "Results / Pending Results",
    "Rejected", // Maps to REJECTED
  ];

  const receivedTableData = [
    {
      id: "LT-00001",
      name: "Mohamed Hassan",
      requestDate: "20-12-2025",
      preferredDate: "20-12-2025",
      preferredTimeSlot: "Morning (8:00 AM - 12:00 PM)",
      urgency: null,
      preferredGender: "Male",
      testType: "Lab",
      preferredLocation: "Home Visit",
    },
    {
      id: "LT-00001",
      name: "Lyla Khalid",
      requestDate: "20-12-2025",
      preferredDate: "20-12-2025",
      preferredTimeSlot: "Morning (8:00 AM - 12:00 PM)",
      urgency: null,
      preferredGender: null,
      testType: "Lab",
      preferredLocation: "On-Site",
    },
    {
      id: "LT-00001",
      name: "Kamla Khalid",
      requestDate: "20-12-2025",
      preferredDate: "20-12-2025",
      preferredTimeSlot: "Morning (8:00 AM - 12:00 PM)",
      urgency: null,
      preferredGender: "Male",
      testType: "Lab",
      preferredLocation: "Home Visit",
    },
    {
      id: "LT-00001",
      name: "Ruby",
      requestDate: "20-12-2025",
      preferredDate: "20-12-2025",
      preferredTimeSlot: "Morning (8:00 AM - 12:00 PM)",
      urgency: null,
      preferredGender: null,
      testType: "Lab",
      preferredLocation: "On-Site",
    },
    {
      id: "LT-00001",
      name: "Kyle",
      requestDate: "20-02-2025",
      preferredDate: "20-02-2025",
      preferredTimeSlot: "Morning (8:00 AM - 12:00 PM)",
      urgency: "Urgent",
      preferredGender: null,
      testType: "Imaging",
      preferredLocation: "On-Site",
    },
    {
      id: "LT-00001",
      name: "Olivia",
      requestDate: "20-02-2025",
      preferredDate: "20-02-2025",
      preferredTimeSlot: "Morning (8:00 AM - 12:00 PM)",
      urgency: null,
      preferredGender: "Male",
      testType: "Lab",
      preferredLocation: "Home Visit",
    },
    {
      id: "LT-00001",
      name: "Ruddy",
      requestDate: "20-02-2025",
      preferredDate: "20-02-2025",
      preferredTimeSlot: "Morning (8:00 AM - 12:00 PM)",
      urgency: null,
      preferredGender: "Male",
      testType: "Lab",
      preferredLocation: "Home Visit",
    },
    {
      id: "LT-00001",
      name: "Kevin",
      requestDate: "20-02-2025",
      preferredDate: "20-02-2025",
      preferredTimeSlot: "Morning (8:00 AM - 12:00 PM)",
      urgency: "Urgent",
      preferredGender: null,
      testType: "Imaging",
      preferredLocation: "On-Site",
    },
    {
      id: "LT-00001",
      name: "Rishabh",
      requestDate: "20-02-2025",
      preferredDate: "20-02-2025",
      preferredTimeSlot: "Morning (8:00 AM - 12:00 PM)",
      urgency: "Urgent",
      preferredGender: null,
      testType: "Imaging",
      preferredLocation: "On-Site",
    },
    {
      id: "LT-00001",
      name: "Rohan",
      requestDate: "20-02-2025",
      preferredDate: "20-02-2025",
      preferredTimeSlot: "Morning (8:00 AM - 12:00 PM)",
      urgency: "Urgent",
      preferredGender: null,
      testType: "Imaging",
      preferredLocation: "On-Site",
    },
  ];

  const awaitingPatientConfirmationTableData = [
    {
      id: "LT-00001",
      name: "Mohamed Hassan",
      requestDate: "20-12-2025",
      testName: "Vitamin D, Complete Blood Count (CBC)",
      testType: "Lab",
      preferredDate: "20-12-2025",
      preferredTimeSlot: "Morning (8:00 AM)",
      urgency: null,
      preferredGender: "Male",
      preferredLocation: "Home Visit",
    },
    {
      id: "LT-00001",
      name: "Lyla Khalid",
      requestDate: "20-12-2025",
      testName: "Vitamin D, Complete Blood Count (CBC)",
      testType: "Lab",
      preferredDate: "20-12-2025",
      preferredTimeSlot: "Morning (8:00 AM)",
      urgency: null,
      preferredGender: null,
      preferredLocation: "On-Site",
    },
    {
      id: "LT-00001",
      name: "Kamla Khalid",
      requestDate: "20-12-2025",
      testName: "Vitamin D, Complete Blood Count (CBC)",
      testType: "Lab",
      preferredDate: "20-12-2025",
      preferredTimeSlot: "Morning (8:00 AM)",
      urgency: null,
      preferredGender: "Male",
      preferredLocation: "Home Visit",
    },
    {
      id: "LT-00001",
      name: "Ruby",
      requestDate: "20-12-2025",
      testName: "Vitamin D, Complete Blood Count (CBC)",
      testType: "Lab",
      preferredDate: "20-12-2025",
      preferredTimeSlot: "Morning (8:00 AM)",
      urgency: null,
      preferredGender: null,
      preferredLocation: "On-Site",
    },
    {
      id: "LT-00001",
      name: "Kyle",
      requestDate: "20-02-2025",
      testName: "Vitamin D, Complete Blood Count (CBC)",
      testType: "Lab",
      preferredDate: "20-02-2025",
      preferredTimeSlot: "Morning (8:00 AM)",
      urgency: "Urgent",
      preferredGender: null,
      preferredLocation: "On-Site",
    },
    {
      id: "LT-00001",
      name: "Olivia",
      requestDate: "20-02-2025",
      testName: "Vitamin D, Complete Blood Count (CBC)",
      testType: "Lab",
      preferredDate: "20-02-2025",
      preferredTimeSlot: "Morning (8:00 AM)",
      urgency: null,
      preferredGender: "Male",
      preferredLocation: "Home Visit",
    },
    {
      id: "LT-00001",
      name: "Ruddy",
      requestDate: "20-02-2025",
      testName: "Vitamin D, Complete Blood Count (CBC)",
      testType: "Lab",
      preferredDate: "20-02-2025",
      preferredTimeSlot: "Morning (8:00 AM)",
      urgency: null,
      preferredGender: "Male",
      preferredLocation: "Home Visit",
    },
    {
      id: "LT-00001",
      name: "Kevin",
      requestDate: "20-02-2025",
      testName: "Vitamin D, Complete Blood Count (CBC)",
      testType: "Lab",
      preferredDate: "20-02-2025",
      preferredTimeSlot: "Morning (8:00 AM)",
      urgency: "Urgent",
      preferredGender: null,
      preferredLocation: "On-Site",
    },
    {
      id: "LT-00001",
      name: "Rishabh",
      requestDate: "20-02-2025",
      testName: "Vitamin D, Complete Blood Count (CBC)",
      testType: "Lab",
      preferredDate: "20-02-2025",
      preferredTimeSlot: "Morning (8:00 AM)",
      urgency: "Urgent",
      preferredGender: null,
      preferredLocation: "On-Site",
    },
    {
      id: "LT-00001",
      name: "Rohan",
      requestDate: "20-02-2025",
      testName: "Vitamin D, Complete Blood Count (CBC)",
      testType: "Lab",
      preferredDate: "20-02-2025",
      preferredTimeSlot: "Morning (8:00 AM)",
      urgency: "Urgent",
      preferredGender: null,
      preferredLocation: "On-Site",
    },
  ];

  const generateInvoiceTableData = [
    {
      id: "LT-00001",
      name: "Mohamed Hassan",
      testName: "Vitamin D, Complete Blood Count (CBC)",
      serviceDate: "20-12-2025",
      serviceTime: "Morning, 10:00 AM",
      testType: "Lab",
      urgency: null,
      location: "Home Visit",
    },
    {
      id: "LT-00001",
      name: "Lyla Khalid",
      testName: "Urine Test",
      serviceDate: "20-12-2025",
      serviceTime: "Morning, 10:00 AM",
      testType: "Lab",
      urgency: null,
      location: "On-Site",
    },
    {
      id: "LT-00001",
      name: "Kamla Khalid",
      testName: "Urine Test",
      serviceDate: "20-12-2025",
      serviceTime: "Morning, 10:00 AM",
      testType: "Lab",
      urgency: null,
      location: "Home Visit",
    },
    {
      id: "LT-00001",
      name: "Ruby",
      testName: "CBC",
      serviceDate: "20-12-2025",
      serviceTime: "Morning, 10:00 AM",
      testType: "Lab",
      urgency: null,
      location: "On-Site",
    },
    {
      id: "LT-00001",
      name: "Kyle",
      testName: "MRI Brain",
      serviceDate: "20-02-2025",
      serviceTime: "Morning, 10:00 AM",
      testType: "Imaging",
      urgency: "Urgent",
      location: "On-Site",
    },
    {
      id: "LT-00001",
      name: "Olivia",
      testName: "CBC",
      serviceDate: "20-02-2025",
      serviceTime: "Morning, 10:00 AM",
      testType: "Lab",
      urgency: null,
      location: "Home Visit",
    },
    {
      id: "LT-00001",
      name: "Ruddy",
      testName: "CBC",
      serviceDate: "20-02-2025",
      serviceTime: "Morning, 10:00 AM",
      testType: "Lab",
      urgency: null,
      location: "Home Visit",
    },
    {
      id: "LT-00001",
      name: "Kevin",
      testName: "CT Scan",
      serviceDate: "20-02-2025",
      serviceTime: "Morning, 10:00 AM",
      testType: "Imaging",
      urgency: "Urgent",
      location: "On-Site",
    },
    {
      id: "LT-00001",
      name: "Rishabh",
      testName: "MRI Brain",
      serviceDate: "20-02-2025",
      serviceTime: "Morning, 10:00 AM",
      testType: "Imaging",
      urgency: "Urgent",
      location: "On-Site",
    },
    {
      id: "LT-00001",
      name: "Rohan",
      testName: "CT Scan",
      serviceDate: "20-02-2025",
      serviceTime: "Morning, 10:00 AM",
      testType: "Imaging",
      urgency: "Urgent",
      location: "On-Site",
    },
  ];

  const awaitingPaymentTableData = [
    {
      id: "LT-0001",
      name: "Mohamed Hassan",
      testName: "Vitamin D, Complete Blood Count",
      serviceDate: "20-12-2025",
      serviceTime: "Morning, 10:00 AM",
      testType: "Lab",
      urgency: "",
      technician: "Rahul Mehta",
      location: "Home Visit",
      totalPrice: "15,000",
    },
    {
      id: "LT-0002",
      name: "Lyla Khalid",
      testName: "Urine Test",
      serviceDate: "20-12-2025",
      serviceTime: "Morning, 10:00 AM",
      testType: "Lab",
      urgency: "",
      technician: "-",
      location: "On-Site",
      totalPrice: "30,000",
    },
    {
      id: "LT-0003",
      name: "Kamla Khalid",
      testName: "Urine Test",
      serviceDate: "20-12-2025",
      serviceTime: "Morning, 10:00 AM",
      testType: "Lab",
      urgency: "",
      technician: "Casie Philip",
      location: "Home Visit",
      totalPrice: "30,000",
    },
    {
      id: "LT-0004",
      name: "Ruby",
      testName: "CBC",
      serviceDate: "20-12-2025",
      serviceTime: "Morning, 10:00 AM",
      testType: "Lab",
      urgency: "",
      technician: "-",
      location: "On-Site",
      totalPrice: "10,000",
    },
    {
      id: "LT-0005",
      name: "Kyle",
      testName: "MRI Brain",
      serviceDate: "20-02-2025",
      serviceTime: "Morning, 10:00 AM",
      testType: "Imaging",
      urgency: "Urgent",
      technician: "-",
      location: "On-Site",
      totalPrice: "1,50,000",
    },
    {
      id: "LT-0006",
      name: "Olivia",
      testName: "CBC",
      serviceDate: "20-02-2025",
      serviceTime: "Morning, 10:00 AM",
      testType: "Lab",
      urgency: "",
      technician: "Naman Bansal",
      location: "Home Visit",
      totalPrice: "10,000",
    },
    {
      id: "LT-0007",
      name: "Ruddy",
      testName: "CBC",
      serviceDate: "10-02-2025",
      serviceTime: "Morning, 10:00 AM",
      testType: "Lab",
      urgency: "",
      technician: "Rohit Kapoor",
      location: "Home Visit",
      totalPrice: "10,000",
    },
    {
      id: "LT-0008",
      name: "Kevin",
      testName: "CT Scan",
      serviceDate: "20-02-2025",
      serviceTime: "Morning, 10:00 AM",
      testType: "Imaging",
      urgency: "Urgent",
      technician: "-",
      location: "On-Site",
      totalPrice: "1,00,000",
    },
    {
      id: "LT-0009",
      name: "Rishabh",
      testName: "MRI Brain",
      serviceDate: "20-02-2025",
      serviceTime: "Morning, 10:00 AM",
      testType: "Imaging",
      urgency: "Urgent",
      technician: "-",
      location: "On-Site",
      totalPrice: "1,50,000",
    },
    {
      id: "LT-0010",
      name: "Rohan",
      testName: "CT Scan",
      serviceDate: "20-02-2025",
      serviceTime: "Morning, 10:00 AM",
      testType: "Imaging",
      urgency: "Urgent",
      technician: "-",
      location: "On-Site",
      totalPrice: "1,00,000",
    },
  ];

  const inProgressTableData = [
    {
      id: "LT-00001",
      name: "Mohamed Hassan",
      testName: "Vitamin D, Complete...",
      serviceDate: "20-12-2025",
      serviceTime: "Morning, 10:00 AM",
      testType: "Lab",
      urgency: "",
      location: "Home Visit",
      sampleStatus: "Pending",
      paymentStatus: "COD",
      technicianStatus: "Technician Dispatched",
    },
    {
      id: "LT-00001",
      name: "Lyla Khalid",
      testName: "Urine Test",
      serviceDate: "20-12-2025",
      serviceTime: "Morning, 10:00 AM",
      testType: "Lab",
      urgency: "",
      location: "On-Site",
      sampleStatus: "Pending",
      paymentStatus: "COD",
      technicianStatus: "Technician Dispatched",
    },
    {
      id: "LT-00001",
      name: "Kamla Khalid",
      testName: "Urine Test",
      serviceDate: "20-12-2025",
      serviceTime: "Morning, 10:00 AM",
      testType: "Lab",
      urgency: "",
      location: "Home Visit",
      sampleStatus: "Pending",
      paymentStatus: "COD",
      technicianStatus: "Technician Dispatched",
    },
    {
      id: "LT-00001",
      name: "Ruby",
      testName: "CBC",
      serviceDate: "20-12-2025",
      serviceTime: "Morning, 10:00 AM",
      testType: "Lab",
      urgency: "",
      location: "On-Site",
      sampleStatus: "Pending",
      paymentStatus: "COD",
      technicianStatus: "",
    },
    {
      id: "LT-00001",
      name: "Kyle",
      testName: "MRI Brain",
      serviceDate: "20-02-2025",
      serviceTime: "Morning, 10:00 AM",
      testType: "Imaging",
      urgency: "Urgent",
      location: "On-Site",
      sampleStatus: "Pending",
      paymentStatus: "Payment Complete",
      technicianStatus: "Technician Dispatched",
    },
    {
      id: "LT-00001",
      name: "Olivia",
      testName: "CBC",
      serviceDate: "20-02-2025",
      serviceTime: "Morning, 10:00 AM",
      testType: "Lab",
      urgency: "",
      location: "Home Visit",
      sampleStatus: "Collected",
      paymentStatus: "Payment Complete",
      technicianStatus: "Technician Dispatched",
    },
    {
      id: "LT-00001",
      name: "Ruddy",
      testName: "CBC",
      serviceDate: "20-02-2025",
      serviceTime: "Morning, 10:00 AM",
      testType: "Lab",
      urgency: "",
      location: "Home Visit",
      sampleStatus: "Collected",
      paymentStatus: "Payment Complete",
      technicianStatus: "Technician Dispatched",
    },
    {
      id: "LT-00001",
      name: "Kevin",
      testName: "CT-Scan",
      serviceDate: "20-02-2025",
      serviceTime: "Morning, 10:00 AM",
      testType: "Imaging",
      urgency: "Urgent",
      location: "On-Site",
      sampleStatus: "Collected",
      paymentStatus: "Payment Complete",
      technicianStatus: "",
    },
    {
      id: "LT-00001",
      name: "Rishabh",
      testName: "MRI Brain",
      serviceDate: "20-02-2025",
      serviceTime: "Morning, 10:00 AM",
      testType: "Imaging",
      urgency: "Urgent",
      location: "On-Site",
      sampleStatus: "Collected",
      paymentStatus: "Payment Complete",
      technicianStatus: "",
    },
    {
      id: "LT-00001",
      name: "Rohan",
      testName: "CT Scan",
      serviceDate: "20-02-2025",
      serviceTime: "Morning, 10:00 AM",
      testType: "Imaging",
      urgency: "Urgent",
      location: "On-Site",
      sampleStatus: "Collected",
      paymentStatus: "Payment Complete",
      technicianStatus: "",
    },
  ];

  return (
    <LabDashboardLayout
      currentPage="lab_ordermanagement"
      onNavigate={handleNavigate}
      onLogout={onLogout}
    >
      {/* Front-face service-name tooltip (position:fixed, never clipped by the
          table's horizontal scroll box). Works across all tabs. */}
      <ServiceNameTooltip styles={styles} />
      <DialogReschedule
        open={openDialogReschedule}
        onClose={() => setOpenDialogReschedule(false)}
        children={undefined}
        selectedAppointment={selectedAppointment}
      />
      <DialogCreateFollowUp
        open={openDialogCreateFollowUp}
        onClose={() => {
          setOpenDialogCreateFollowUp(false);
        }}
        children={undefined}
        selectedAppointment={selectedAppointment}
      />
      <DialogManageAvailability
        open={openDialogManageAvailability}
        onClose={() => {
          setOpenDialogManageAvailability(false);
        }}
        children={undefined}
      />
      <DialogPatientRegistration
        open={openDialogPatientRegistration}
        onClose={() => {
          setOpenDialogPatientRegistration(false);
        }}
        children={undefined}
      />

      {showDetails ? (
        <AppointmentDetails
          handleBack={() => setShowDetails(false)}
          homeVisit={false}
          selectedAppointment={selectedAppointment}
        />
      ) : (
        <>
          <div className={styles["apt-page-header"]}>
            <h1>{t("lab.orderManagement.title")}</h1>
          </div>
          <div className={styles["apt-appointments-section"]}>
            <div className={styles["apt-section-header"]}>
              <h2>{t("lab.orderManagement.section.title")}</h2>
              <div className={styles["apt-top-actions"]}>
                <button className={styles["exportBtn"]} onClick={handleExport}>
                  <FiDownload /> {t("lab.orderManagement.button.export")}
                </button>
              </div>
            </div>

            {/* Filters */}
            <div className={styles["apt-filters"]}>
              <div className={styles["apt-filter-group"]}>
                {/* <span className={styles["apt-filter-icon-wrap"]}>
                  <Filter className={styles["apt-filter-icon"]} />
                </span> */}
                <div
                  className={styles["apt-search-wrapper"]}
                  style={{ position: "relative" }}
                >
                  <Search className={styles["apt-search-icon"]} />
                  <input
                    type="text"
                    placeholder={t(
                      "lab.orderManagement.filters.searchPlaceholder",
                    )}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    onFocus={() => {
                      if (searchResults.length > 0) setShowSearchDropdown(true);
                    }}
                    onBlur={() =>
                      setTimeout(() => setShowSearchDropdown(false), 150)
                    }
                    className={styles["apt-search-input"]}
                  />
                  {showSearchDropdown && searchTerm.trim() && (
                    <div
                      style={{
                        position: "absolute",
                        top: "calc(100% + 4px)",
                        left: 0,
                        right: 0,
                        zIndex: 50,
                        background: "#fff",
                        border: "1px solid #e5e7eb",
                        borderRadius: "8px",
                        boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
                        maxHeight: "300px",
                        overflowY: "auto",
                      }}
                    >
                      {searchLoading ? (
                        <div
                          style={{
                            padding: "12px 14px",
                            fontSize: "13px",
                            color: "#6b7280",
                          }}
                        >
                          Searching...
                        </div>
                      ) : searchResults.length === 0 ? (
                        <div
                          style={{
                            padding: "12px 14px",
                            fontSize: "13px",
                            color: "#6b7280",
                          }}
                        >
                          No orders found
                        </div>
                      ) : (
                        searchResults.map((item) => {
                          const p = item.patient;
                          const guardianName = p
                            ? `${p.full_name || ""} ${p.surname || ""}`.trim()
                            : "";
                          // Child bookings: surface the child's name in the
                          // search dropdown too, so the officer can find and
                          // click through to the correct row using the child's
                          // name (matches the Name column shown in the table).
                          const isChildFlow =
                            item?.is_for_child === true ||
                            item?.isForChild === true;
                          const childName =
                            [
                              item?.child_name || item?.childName,
                              item?.child_surname,
                            ]
                              .filter(Boolean)
                              .join(" ") || null;
                          const pName =
                            isChildFlow && childName ? childName : guardianName;
                          return (
                            <button
                              type="button"
                              key={item.id}
                              onMouseDown={(e) => {
                                e.preventDefault();
                                setShowSearchDropdown(false);
                                navigate(
                                  `/lab_appointment_detail/${item.id}?source=ordermanagement`,
                                );
                              }}
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "10px",
                                width: "100%",
                                padding: "10px 14px",
                                background: "none",
                                border: "none",
                                borderBottom: "1px solid #f3f4f6",
                                cursor: "pointer",
                                textAlign: "left",
                              }}
                            >
                              <div style={{ minWidth: 0 }}>
                                <div
                                  style={{
                                    fontSize: "13px",
                                    fontWeight: 600,
                                    color: "#111827",
                                    whiteSpace: "nowrap",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                  }}
                                >
                                  {pName || `Patient ${item.patientId || ""}`}
                                </div>
                                <div
                                  style={{ fontSize: "12px", color: "#6b7280" }}
                                >
                                  {item.bookingNumber || `#${item.id}`}
                                </div>
                              </div>
                            </button>
                          );
                        })
                      )}
                    </div>
                  )}
                </div>
                <select
                  value={locationFilter}
                  onChange={(e) => {
                    const val = e.target.value;
                    setLocationFilter(val);
                    setCurrentPage(1);
                    fetchAppointmentsWithFilters(
                      1,
                      undefined,
                      undefined,
                      undefined,
                      undefined,
                      val,
                    );
                  }}
                  className={styles["apt-status-filter"]}
                  style={{ paddingRight: "30px" }}
                >
                  <option value="">All Locations</option>
                  <option value="HOME_VISIT">Home Visit</option>
                  <option value="ON_SITE">On-Site</option>
                </select>
                {/* Test-type filter dropdown hidden per product request.
                    The lab app only sees Lab bookings (Imaging bookings are
                    served from the imaging surface), so the Lab / Imaging /
                    All Test Types switch was redundant. The `testTypeFilter`
                    state + reset + query wiring is deliberately kept so the
                    filter can be brought back with a one-line JSX restore
                    if needed; `All` remains the effective default and gets
                    stripped from the query params by `fetchAppointmentsWithFilters`. */}
                <div className={styles["date-wrapper"]}>
                  <DatePicker
                    key={`datepicker-${date ? date.getTime() : "empty"}`}
                    className={styles["apt-date-filter"]}
                    selected={date}
                    onChange={(d) => {
                      setDate(d);
                      setCurrentPage(1);
                      fetchAppointmentsWithFilters(1, undefined, d);
                    }}
                    placeholderText={t(
                      "lab.orderManagement.filters.selectDate",
                    )}
                    isClearable
                  />
                </div>
                <button
                  className={styles["apt-apply-btn"]}
                  onClick={() => {
                    setCurrentPage(1);
                    fetchAppointmentsWithFilters(1);
                  }}
                >
                  {t("lab.orderManagement.filters.apply")}
                </button>
                <button
                  className={styles["apt-reset-btn"]}
                  onClick={resetFilters}
                >
                  {t("lab.orderManagement.filters.reset")}
                </button>
              </div>
            </div>

            <div className={styles["apt-appointment-tabs"]}>
              {orderManagementTabs.map((tabItem, idx) => {
                return (
                  <button
                    key={idx}
                    className={`${styles["apt-tab"]} ${
                      activeTab === tabItem ? styles["active"] : ""
                    }`}
                    onClick={() => {
                      setActiveTab(tabItem);
                      setStatusFilter(tabItem);
                      setCurrentPage(1);
                      fetchAppointmentsWithFilters(1, tabItem);
                    }}
                  >
                    {t(getTabTranslationKey(tabItem), tabItem)}
                  </button>
                );
              })}
            </div>

            {/* Table */}
            {activeTab === "Received" && (
              <div className={styles["apt-appointments-table"]}>
                <table>
                  <thead>
                    <tr>
                      <th>{t("lab.orderManagement.table.id")}</th>
                      <th>{t("lab.orderManagement.table.name")}</th>
                      {/* Child bookings treat the CHILD as the patient, so the child gets its own
                          column rather than being conflated with the account holder. */}
                      <th>{t("childBooking.childName", "Child Name")}</th>
                      {renderSortableHeader(
                        "requestDateRaw",
                        t("lab.orderManagement.table.requestDate"),
                      )}
                      <th>{t("lab.orderManagement.table.preferredDate")}</th>
                      <th>
                        {t("lab.orderManagement.table.preferredTimeSlot")}
                      </th>
                      <th>{t("lab.orderManagement.table.urgency")}</th>
                      <th>{t("lab.orderManagement.table.preferredGender")}</th>
                      <th>{t("lab.orderManagement.table.testType")}</th>
                      <th>
                        {t("lab.orderManagement.table.preferredLocation")}
                      </th>
                      <th>
                        {t("lab.orderManagement.table.slaTime", "SLA Time")}
                      </th>
                      <th>{t("lab.orderManagement.table.action")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loading ? (
                      <tr>
                        <td colSpan={12}>
                          <Loader />
                        </td>
                      </tr>
                    ) : paginatedAppointments.length === 0 ? (
                      <tr>
                        <td
                          colSpan={12}
                          style={{ textAlign: "center", padding: "20px" }}
                        >
                          {t("lab.orderManagement.noData.orders")}
                        </td>
                      </tr>
                    ) : (
                      paginatedAppointments.map((item, idx) => (
                        <tr key={idx}>
                          <td>{item.bookingNumber || item.id}</td>
                          <td>
                            <BookingPatientNameCell
                              childName={item.childName}
                              name={item.guardianName}
                            />
                          </td>
                          <td>
                            <BookingChildNameCell childName={item.childName} />
                          </td>
                          <td>{item.requestDate || "N/A"}</td>
                          <td>{item.preferredDate || "N/A"}</td>
                          <td>{item.preferredTimeSlot || "N/A"}</td>
                          <td>
                            {item.urgency ? (
                              <span
                                className={`${styles["db-status"]} ${
                                  styles[getStatusColor(item.urgency)]
                                }`}
                              >
                                {item.urgency}
                              </span>
                            ) : (
                              <span
                                className={`${styles["db-status"]} ${styles["status-noturgent"]}`}
                              >
                                Not Urgent
                              </span>
                            )}
                          </td>
                          <td>{item.preferredGender || "-"}</td>
                          <td>
                            {item.testType ? (
                              <span
                                className={`${styles["db-status"]} ${
                                  styles[getStatusColor(item.testType)]
                                }`}
                              >
                                {item.testType}
                              </span>
                            ) : (
                              "Lab"
                            )}
                          </td>
                          <td>
                            {item.preferredLocation ? (
                              <span
                                className={`${styles["db-status"]} ${
                                  styles[
                                    getStatusColor(
                                      formatLocation(item.preferredLocation),
                                    )
                                  ]
                                }`}
                              >
                                {formatLocation(item.preferredLocation)}
                              </span>
                            ) : (
                              "N/A"
                            )}
                          </td>
                          <td>
                            <SlaTimer
                              dueAt={item.slaDueAt}
                              isUrgent={item.slaIsUrgent}
                              status={item.status}
                            />
                          </td>
                          <td>
                            <div className={styles["apt-action-buttons"]}>
                              <Link
                                to={`/lab_appointment_detail/${item.id}?source=ordermanagement`}
                              >
                                <button
                                  className={styles["apt-view-btn"]}
                                  title="View"
                                >
                                  <svg
                                    width="24"
                                    height="24"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                  >
                                    <path
                                      fillRule="evenodd"
                                      clipRule="evenodd"
                                      d="M12 8.25C11.0054 8.25 10.0516 8.64509 9.34835 9.34835C8.64509 10.0516 8.25 11.0054 8.25 12C8.25 12.9946 8.64509 13.9484 9.34835 14.6516C10.0516 15.3549 11.0054 15.75 12 15.75C12.9946 15.75 13.9484 15.3549 14.6516 14.6516C15.3549 13.9484 15.75 12.9946 15.75 12C15.75 11.0054 15.3549 10.0516 14.6516 9.34835C13.9484 8.64509 12.9946 8.25 12 8.25ZM9.75 12C9.75 11.4033 9.98705 10.831 10.409 10.409C10.831 9.98705 11.4033 9.75 12 9.75C12.5967 9.75 13.169 9.98705 13.591 10.409C14.0129 10.831 14.25 11.4033 14.25 12C14.25 12.5967 14.0129 13.169 13.591 13.591C13.169 14.0129 12.5967 14.25 12 14.25C11.4033 14.25 10.831 14.0129 10.409 13.591C9.98705 13.169 9.75 12.5967 9.75 12Z"
                                      fill="#155DFC"
                                    />
                                    <path
                                      fillRule="evenodd"
                                      clipRule="evenodd"
                                      d="M12 3.25C7.486 3.25 4.445 5.954 2.68 8.247L2.649 8.288C2.249 8.807 1.882 9.284 1.633 9.848C1.366 10.453 1.25 11.112 1.25 12C1.25 12.888 1.366 13.547 1.633 14.152C1.883 14.716 2.25 15.194 2.649 15.712L2.681 15.753C4.445 18.046 7.486 20.75 12 20.75C16.514 20.75 19.555 18.046 21.32 15.753L21.351 15.712C21.751 15.194 22.118 14.716 22.367 14.152C22.634 13.547 22.75 12.888 22.75 12C22.75 11.112 22.634 10.453 22.367 9.848C22.117 9.284 21.75 8.807 21.351 8.288L21.319 8.247C19.555 5.954 16.514 3.25 12 3.25ZM3.87 9.162C5.498 7.045 8.15 4.75 12 4.75C15.85 4.75 18.501 7.045 20.13 9.162C20.57 9.732 20.826 10.072 20.995 10.454C21.153 10.812 21.25 11.249 21.25 12C21.25 12.751 21.153 13.188 20.995 13.546C20.826 13.928 20.569 14.268 20.131 14.838C18.5 16.955 15.85 19.25 12 19.25C8.15 19.25 5.499 16.955 3.87 14.838C3.43 14.268 3.174 13.928 3.005 13.546C2.847 13.188 2.75 12.751 2.75 12C2.75 11.249 2.847 10.812 3.005 10.454C3.174 10.072 3.432 9.732 3.87 9.162Z"
                                      fill="#155DFC"
                                    />
                                  </svg>
                                </button>
                              </Link>

                              {PRINTABLE_STATUSES.includes(
                                item.status ?? item.raw?.status,
                              ) && (
                                <button
                                  className={styles["apt-view-btn"]}
                                  title="Print Invoice"
                                  onClick={() => openInvoice(item)}
                                  style={{
                                    background: "none",
                                    border: "none",
                                    cursor: "pointer",
                                  }}
                                >
                                  <FiPrinter size={18} color="#111827" />
                                </button>
                              )}

                              <ChatIconWithBadge
                                className={styles["apt-view-btn"]}
                                title="Chat"
                                count={unreadForAppointment(
                                  item.raw?.id ??
                                    item.raw?.booking_id ??
                                    item.id,
                                )}
                                onClick={() => openChat(item)}
                              />
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            )}

            {/* Awaiting Confirmation Tab */}
            {activeTab === "Awaiting Confirmation" && (
              <div className={styles["apt-appointments-table"]}>
                <table>
                  <thead>
                    <tr>
                      <th>{t("lab.orderManagement.table.id")}</th>
                      <th>{t("lab.orderManagement.table.name")}</th>
                      {/* Child bookings treat the CHILD as the patient, so the child gets its own
                          column rather than being conflated with the account holder. */}
                      <th>{t("childBooking.childName", "Child Name")}</th>
                      {renderSortableHeader(
                        "requestDateRaw",
                        t("lab.orderManagement.table.requestDate"),
                      )}
                      <th>{t("lab.orderManagement.table.testName")}</th>
                      <th>{t("lab.orderManagement.table.testType")}</th>
                      <th>{t("lab.orderManagement.table.preferredDate")}</th>
                      <th>
                        {t("lab.orderManagement.table.preferredTimeSlot")}
                      </th>
                      <th>{t("lab.orderManagement.table.urgency")}</th>
                      <th>{t("lab.orderManagement.table.preferredGender")}</th>
                      <th>
                        {t("lab.orderManagement.table.preferredLocation")}
                      </th>
                      <th>{t("lab.orderManagement.table.action")}</th>
                    </tr>
                  </thead>

                  <tbody>
                    {loading ? (
                      <tr>
                        <td colSpan={12}>
                          <Loader />
                        </td>
                      </tr>
                    ) : paginatedAppointments.length === 0 ? (
                      <tr>
                        <td
                          colSpan={12}
                          style={{ textAlign: "center", padding: "20px" }}
                        >
                          {t("lab.orderManagement.noData.records")}
                        </td>
                      </tr>
                    ) : (
                      paginatedAppointments.map((item, idx) => (
                        <tr key={idx}>
                          <td>{item.bookingNumber || item.id}</td>
                          <td>
                            <BookingPatientNameCell
                              childName={item.childName}
                              name={item.guardianName}
                            />
                          </td>
                          <td>
                            <BookingChildNameCell childName={item.childName} />
                          </td>
                          <td>{item.requestDate}</td>
                          <td>
                            {item.testName && item.testName.length > 20 ? (
                              <div className={styles["testNames"]}>
                                <div className={styles["truncateTestNames"]}>
                                  {item.testName}
                                </div>
                                <div className={styles["testNamesList"]}>
                                  <ul>
                                    <li>
                                      <span>
                                        {t(
                                          "lab.orderManagement.testNames.tests",
                                        )}
                                      </span>
                                    </li>
                                    {item.testName
                                      .split(",")
                                      .map((test, index) => (
                                        <li key={index}>{test.trim()}</li>
                                      ))}
                                  </ul>
                                </div>
                              </div>
                            ) : (
                              item.testName || "N/A"
                            )}
                          </td>

                          <td>
                            {item.testType ? (
                              <span
                                className={`${styles["db-status"]} ${
                                  styles[getStatusColor(item.testType)]
                                }`}
                              >
                                {item.testType}
                              </span>
                            ) : (
                              "Lab"
                            )}
                          </td>

                          <td>{item.preferredDate}</td>
                          <td>{item.preferredTimeSlot}</td>
                          <td>
                            {item.urgency ? (
                              <span
                                className={`${styles["db-status"]} ${
                                  styles[getStatusColor(item.urgency)]
                                }`}
                              >
                                {item.urgency}
                              </span>
                            ) : (
                              <span
                                className={`${styles["db-status"]} ${styles["status-noturgent"]}`}
                              >
                                Not Urgent
                              </span>
                            )}
                          </td>
                          <td>
                            {item.preferredGender ? (
                              <span
                                className={`${styles["db-status"]} ${
                                  styles[getStatusColor(item.preferredGender)]
                                }`}
                              >
                                {item.preferredGender}
                              </span>
                            ) : (
                              "-"
                            )}
                          </td>
                          <td>
                            {item.preferredLocation ? (
                              <span
                                className={`${styles["db-status"]} ${
                                  styles[
                                    getStatusColor(
                                      formatLocation(item.preferredLocation),
                                    )
                                  ]
                                }`}
                              >
                                {formatLocation(item.preferredLocation)}
                              </span>
                            ) : (
                              "N/A"
                            )}
                          </td>
                          <td>
                            <div className={styles["apt-action-buttons"]}>
                              <Link
                                to={`/lab_appointment_detail/${item.id}?source=ordermanagement`}
                              >
                                <button
                                  className={styles["apt-view-btn"]}
                                  title="View"
                                >
                                  <svg
                                    width="24"
                                    height="24"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                  >
                                    <path
                                      fillRule="evenodd"
                                      clipRule="evenodd"
                                      d="M12 8.25C11.0054 8.25 10.0516 8.64509 9.34835 9.34835C8.64509 10.0516 8.25 11.0054 8.25 12C8.25 12.9946 8.64509 13.9484 9.34835 14.6516C10.0516 15.3549 11.0054 15.75 12 15.75C12.9946 15.75 13.9484 15.3549 14.6516 14.6516C15.3549 13.9484 15.75 12.9946 15.75 12C15.75 11.0054 15.3549 10.0516 14.6516 9.34835C13.9484 8.64509 12.9946 8.25 12 8.25ZM9.75 12C9.75 11.4033 9.98705 10.831 10.409 10.409C10.831 9.98705 11.4033 9.75 12 9.75C12.5967 9.75 13.169 9.98705 13.591 10.409C14.0129 10.831 14.25 11.4033 14.25 12C14.25 12.5967 14.0129 13.169 13.591 13.591C13.169 14.0129 12.5967 14.25 12 14.25C11.4033 14.25 10.831 14.0129 10.409 13.591C9.98705 13.169 9.75 12.5967 9.75 12Z"
                                      fill="#155DFC"
                                    />
                                    <path
                                      fillRule="evenodd"
                                      clipRule="evenodd"
                                      d="M12 3.25C7.486 3.25 4.445 5.954 2.68 8.247L2.649 8.288C2.249 8.807 1.882 9.284 1.633 9.848C1.366 10.453 1.25 11.112 1.25 12C1.25 12.888 1.366 13.547 1.633 14.152C1.883 14.716 2.25 15.194 2.649 15.712L2.681 15.753C4.445 18.046 7.486 20.75 12 20.75C16.514 20.75 19.555 18.046 21.32 15.753L21.351 15.712C21.751 15.194 22.118 14.716 22.367 14.152C22.634 13.547 22.75 12.888 22.75 12C22.75 11.112 22.634 10.453 22.367 9.848C22.117 9.284 21.75 8.807 21.351 8.288L21.319 8.247C19.555 5.954 16.514 3.25 12 3.25ZM3.87 9.162C5.498 7.045 8.15 4.75 12 4.75C15.85 4.75 18.501 7.045 20.13 9.162C20.57 9.732 20.826 10.072 20.995 10.454C21.153 10.812 21.25 11.249 21.25 12C21.25 12.751 21.153 13.188 20.995 13.546C20.826 13.928 20.569 14.268 20.131 14.838C18.5 16.955 15.85 19.25 12 19.25C8.15 19.25 5.499 16.955 3.87 14.838C3.43 14.268 3.174 13.928 3.005 13.546C2.847 13.188 2.75 12.751 2.75 12C2.75 11.249 2.847 10.812 3.005 10.454C3.174 10.072 3.432 9.732 3.87 9.162Z"
                                      fill="#155DFC"
                                    />
                                  </svg>
                                </button>
                              </Link>

                              {PRINTABLE_STATUSES.includes(
                                item.status ?? item.raw?.status,
                              ) && (
                                <button
                                  className={styles["apt-view-btn"]}
                                  title="Print Invoice"
                                  onClick={() => openInvoice(item)}
                                  style={{
                                    background: "none",
                                    border: "none",
                                    cursor: "pointer",
                                  }}
                                >
                                  <FiPrinter size={18} color="#111827" />
                                </button>
                              )}

                              <ChatIconWithBadge
                                className={styles["apt-view-btn"]}
                                title="Chat"
                                count={unreadForAppointment(
                                  item.raw?.id ??
                                    item.raw?.booking_id ??
                                    item.id,
                                )}
                                onClick={() => openChat(item)}
                              />
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            )}

            {/* Awaiting Payment Tab */}
            {activeTab === "Awaiting Payment" && (
              <div className={styles["apt-appointments-table"]}>
                <table>
                  <thead>
                    <tr>
                      <th>{t("lab.orderManagement.table.id")}</th>
                      <th>{t("lab.orderManagement.table.name")}</th>
                      {/* Child bookings treat the CHILD as the patient, so the child gets its own
                          column rather than being conflated with the account holder. */}
                      <th>{t("childBooking.childName", "Child Name")}</th>
                      <th>{t("lab.orderManagement.table.testName")}</th>
                      <th>{t("lab.orderManagement.table.serviceDate")}</th>
                      <th>{t("lab.orderManagement.table.serviceTime")}</th>
                      <th>{t("lab.orderManagement.table.testType")}</th>
                      <th>{t("lab.orderManagement.table.urgency")}</th>
                      <th>{t("lab.orderManagement.table.technician")}</th>
                      <th>{t("lab.orderManagement.table.location")}</th>
                      <th>{t("lab.orderManagement.table.totalPrice")}</th>
                      <th>{t("lab.orderManagement.table.action")}</th>
                    </tr>
                  </thead>

                  <tbody>
                    {loading ? (
                      <tr>
                        <td colSpan={12}>
                          <Loader />
                        </td>
                      </tr>
                    ) : paginatedAppointments.length === 0 ? (
                      <tr>
                        <td
                          colSpan={12}
                          style={{ textAlign: "center", padding: "20px" }}
                        >
                          No records found
                        </td>
                      </tr>
                    ) : (
                      paginatedAppointments.map((item, idx) => (
                        <tr key={idx}>
                          <td>{item.bookingNumber || item.id}</td>
                          <td>
                            <BookingPatientNameCell
                              childName={item.childName}
                              name={item.guardianName}
                            />
                          </td>
                          <td>
                            <BookingChildNameCell childName={item.childName} />
                          </td>

                          {/* Test Name with tooltip list */}
                          <td>
                            {item.testName && item.testName.length > 20 ? (
                              <div className={styles["testNames"]}>
                                <div className={styles["truncateTestNames"]}>
                                  {item.testName}
                                </div>
                                <div className={styles["testNamesList"]}>
                                  <ul>
                                    <li>
                                      <span>
                                        {t(
                                          "lab.orderManagement.testNames.tests",
                                        )}
                                      </span>
                                    </li>
                                    {item.testName
                                      .split(",")
                                      .map((test, index) => (
                                        <li key={index}>{test.trim()}</li>
                                      ))}
                                  </ul>
                                </div>
                              </div>
                            ) : (
                              item.testName || "-"
                            )}
                          </td>

                          <td>{item.serviceDate || "-"}</td>
                          <td>{item.serviceTime || "-"}</td>

                          {/* Test Type */}
                          <td>
                            <span
                              className={`${styles["db-status"]} ${
                                styles[getStatusColor(item.testType)]
                              }`}
                            >
                              {item.testType || "-"}
                            </span>
                          </td>

                          {/* Urgency */}
                          <td>
                            {item.urgency ? (
                              <span
                                className={`${styles["db-status"]} ${
                                  styles[getStatusColor(item.urgency)]
                                }`}
                              >
                                {item.urgency}
                              </span>
                            ) : (
                              <span
                                className={`${styles["db-status"]} ${styles["status-noturgent"]}`}
                              >
                                Not Urgent
                              </span>
                            )}
                          </td>

                          {/* Technician */}
                          <td>{item.technician || "-"}</td>

                          {/* Location */}
                          <td>
                            <span
                              className={`${styles["db-status"]} ${styles[getStatusColor(formatLocation(item.preferredLocation || item.location))]}`}
                            >
                              {formatLocation(
                                item.preferredLocation || item.location,
                              )}
                            </span>
                          </td>

                          {/* Total Price */}
                          <td>
                            {item.totalPrice
                              ? `IQD ${formatAmount(item.totalPrice)}`
                              : "-"}
                          </td>

                          {/* ACTION COLUMN */}
                          <td>
                            <div className={styles["apt-action-buttons"]}>
                              <Link
                                to={`/lab_appointment_detail/${item.id}?source=ordermanagement`}
                              >
                                <button
                                  className={styles["apt-view-btn"]}
                                  title="View"
                                >
                                  <svg
                                    width="24"
                                    height="24"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                  >
                                    <path
                                      fillRule="evenodd"
                                      clipRule="evenodd"
                                      d="M12 8.25C11.0054 8.25 10.0516 8.64509 9.34835 9.34835C8.64509 10.0516 8.25 11.0054 8.25 12C8.25 12.9946 8.64509 13.9484 9.34835 14.6516C10.0516 15.3549 11.0054 15.75 12 15.75C12.9946 15.75 13.9484 15.3549 14.6516 14.6516C15.3549 13.9484 15.75 12.9946 15.75 12C15.75 11.0054 15.3549 10.0516 14.6516 9.34835C13.9484 8.64509 12.9946 8.25 12 8.25ZM9.75 12C9.75 11.4033 9.98705 10.831 10.409 10.409C10.831 9.98705 11.4033 9.75 12 9.75C12.5967 9.75 13.169 9.98705 13.591 10.409C14.0129 10.831 14.25 11.4033 14.25 12C14.25 12.5967 14.0129 13.169 13.591 13.591C13.169 14.0129 12.5967 14.25 12 14.25C11.4033 14.25 10.831 14.0129 10.409 13.591C9.98705 13.169 9.75 12.5967 9.75 12Z"
                                      fill="#155DFC"
                                    />
                                    <path
                                      fillRule="evenodd"
                                      clipRule="evenodd"
                                      d="M12 3.25C7.486 3.25 4.445 5.954 2.68 8.247L2.649 8.288C2.249 8.807 1.882 9.284 1.633 9.848C1.366 10.453 1.25 11.112 1.25 12C1.25 12.888 1.366 13.547 1.633 14.152C1.883 14.716 2.25 15.194 2.649 15.712L2.681 15.753C4.445 18.046 7.486 20.75 12 20.75C16.514 20.75 19.555 18.046 21.32 15.753L21.351 15.712C21.751 15.194 22.118 14.716 22.367 14.152C22.634 13.547 22.75 12.888 22.75 12C22.75 11.112 22.634 10.453 22.367 9.848C22.117 9.284 21.75 8.807 21.351 8.288L21.319 8.247C19.555 5.954 16.514 3.25 12 3.25ZM3.87 9.162C5.498 7.045 8.15 4.75 12 4.75C15.85 4.75 18.501 7.045 20.13 9.162C20.57 9.732 20.826 10.072 20.995 10.454C21.153 10.812 21.25 11.249 21.25 12C21.25 12.751 21.153 13.188 20.995 13.546C20.826 13.928 20.569 14.268 20.131 14.838C18.5 16.955 15.85 19.25 12 19.25C8.15 19.25 5.499 16.955 3.87 14.838C3.43 14.268 3.174 13.928 3.005 13.546C2.847 13.188 2.75 12.751 2.75 12C2.75 11.249 2.847 10.812 3.005 10.454C3.174 10.072 3.432 9.732 3.87 9.162Z"
                                      fill="#155DFC"
                                    />
                                  </svg>
                                </button>
                              </Link>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            )}

            {/* Order Accepted tab  */}
            {activeTab === "Order Accepted" && (
              <div className={styles["apt-appointments-table"]}>
                <table>
                  <thead>
                    <tr>
                      <th>{t("lab.orderManagement.table.id")}</th>
                      <th>{t("lab.orderManagement.table.name")}</th>
                      {/* Child bookings treat the CHILD as the patient, so the child gets its own
                          column rather than being conflated with the account holder. */}
                      <th>{t("childBooking.childName", "Child Name")}</th>
                      <th>{t("lab.orderManagement.table.testName")}</th>
                      <th>{t("lab.orderManagement.table.serviceDate")}</th>
                      <th>{t("lab.orderManagement.table.serviceTime")}</th>
                      <th>{t("lab.orderManagement.table.testType")}</th>
                      <th>{t("lab.orderManagement.table.urgency")}</th>
                      <th>{t("lab.orderManagement.table.technician")}</th>
                      <th>{t("lab.orderManagement.table.location")}</th>
                      <th>{t("lab.orderManagement.table.totalPrice")}</th>
                      <th>{t("lab.orderManagement.table.paymentStatus")}</th>
                      <th>{t("lab.orderManagement.table.action")}</th>
                    </tr>
                  </thead>

                  <tbody>
                    {loading ? (
                      <tr>
                        <td colSpan={13}>
                          <Loader />
                        </td>
                      </tr>
                    ) : paginatedAppointments.length === 0 ? (
                      <tr>
                        <td
                          colSpan={13}
                          style={{ textAlign: "center", padding: "20px" }}
                        >
                          No orders found
                        </td>
                      </tr>
                    ) : (
                      paginatedAppointments.map((item, idx) => (
                        <tr key={idx}>
                          <td>{item.bookingNumber || item.id}</td>
                          <td>
                            <BookingPatientNameCell
                              childName={item.childName}
                              name={item.guardianName}
                            />
                          </td>
                          <td>
                            <BookingChildNameCell childName={item.childName} />
                          </td>

                          <td>
                            {item.testName && item.testName.length > 20 ? (
                              <div className={styles["testNames"]}>
                                <div className={styles["truncateTestNames"]}>
                                  {item.testName}
                                </div>
                                <div className={styles["testNamesList"]}>
                                  <ul>
                                    <li>
                                      <span>
                                        {t(
                                          "lab.orderManagement.testNames.tests",
                                        )}
                                      </span>
                                    </li>
                                    {item.testName
                                      .split(",")
                                      .map((test, index) => (
                                        <li key={index}>{test.trim()}</li>
                                      ))}
                                  </ul>
                                </div>
                              </div>
                            ) : (
                              item.testName || "N/A"
                            )}
                          </td>

                          <td>{item.serviceDate || "N/A"}</td>
                          <td>{item.serviceTime || "N/A"}</td>

                          <td>
                            {item.testType ? (
                              <span
                                className={`${styles["db-status"]} ${styles[getStatusColor(item.testType)]}`}
                              >
                                {item.testType}
                              </span>
                            ) : (
                              "Lab"
                            )}
                          </td>

                          <td>
                            {item.urgency ? (
                              <span
                                className={`${styles["db-status"]} ${styles[getStatusColor(item.urgency)]}`}
                              >
                                {item.urgency}
                              </span>
                            ) : (
                              <span
                                className={`${styles["db-status"]} ${styles["status-noturgent"]}`}
                              >
                                Not Urgent
                              </span>
                            )}
                          </td>

                          <td>{item.technician || "-"}</td>

                          <td>
                            {item.location || item.preferredLocation ? (
                              <span
                                className={`${styles["db-status"]} ${styles[getStatusColor(formatLocation(item.location || item.preferredLocation))]}`}
                              >
                                {formatLocation(
                                  item.location || item.preferredLocation,
                                )}
                              </span>
                            ) : (
                              "N/A"
                            )}
                          </td>

                          <td>IQD {formatAmount(item.totalPrice)}</td>

                          {/* Payment Status */}
                          <td>
                            {item.paymentStatus ? (
                              <span
                                className={`${styles["db-status"]} ${styles[getStatusColor(formatPaymentStatus(item.paymentStatus))]}`}
                              >
                                {formatPaymentStatus(item.paymentStatus)}
                              </span>
                            ) : (
                              "-"
                            )}
                          </td>

                          {/* ACTION COLUMN (UNCHANGED) */}
                          <td>
                            <div className={styles["apt-action-buttons"]}>
                              <Link
                                to={`/lab_appointment_detail/${item.id}?source=ordermanagement`}
                              >
                                <button
                                  className={styles["apt-view-btn"]}
                                  title="View"
                                >
                                  <svg
                                    width="24"
                                    height="24"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                  >
                                    <path
                                      fillRule="evenodd"
                                      clipRule="evenodd"
                                      d="M12 8.25C11.0054 8.25 10.0516 8.64509 9.34835 9.34835C8.64509 10.0516 8.25 11.0054 8.25 12C8.25 12.9946 8.64509 13.9484 9.34835 14.6516C10.0516 15.3549 11.0054 15.75 12 15.75C12.9946 15.75 13.9484 15.3549 14.6516 14.6516C15.3549 13.9484 15.75 12.9946 15.75 12C15.75 11.0054 15.3549 10.0516 14.6516 9.34835C13.9484 8.64509 12.9946 8.25 12 8.25ZM9.75 12C9.75 11.4033 9.98705 10.831 10.409 10.409C10.831 9.98705 11.4033 9.75 12 9.75C12.5967 9.75 13.169 9.98705 13.591 10.409C14.0129 10.831 14.25 11.4033 14.25 12C14.25 12.5967 14.0129 13.169 13.591 13.591C13.169 14.0129 12.5967 14.25 12 14.25C11.4033 14.25 10.831 14.0129 10.409 13.591C9.98705 13.169 9.75 12.5967 9.75 12Z"
                                      fill="#155DFC"
                                    />
                                    <path
                                      fillRule="evenodd"
                                      clipRule="evenodd"
                                      d="M12 3.25C7.486 3.25 4.445 5.954 2.68 8.247L2.649 8.288C2.249 8.807 1.882 9.284 1.633 9.848C1.366 10.453 1.25 11.112 1.25 12C1.25 12.888 1.366 13.547 1.633 14.152C1.883 14.716 2.25 15.194 2.649 15.712L2.681 15.753C4.445 18.046 7.486 20.75 12 20.75C16.514 20.75 19.555 18.046 21.32 15.753L21.351 15.712C21.751 15.194 22.118 14.716 22.367 14.152C22.634 13.547 22.75 12.888 22.75 12C22.75 11.112 22.634 10.453 22.367 9.848C22.117 9.284 21.75 8.807 21.351 8.288L21.319 8.247C19.555 5.954 16.514 3.25 12 3.25ZM3.87 9.162C5.498 7.045 8.15 4.75 12 4.75C15.85 4.75 18.501 7.045 20.13 9.162C20.57 9.732 20.826 10.072 20.995 10.454C21.153 10.812 21.25 11.249 21.25 12C21.25 12.751 21.153 13.188 20.995 13.546C20.826 13.928 20.569 14.268 20.131 14.838C18.5 16.955 15.85 19.25 12 19.25C8.15 19.25 5.499 16.955 3.87 14.838C3.43 14.268 3.174 13.928 3.005 13.546C2.847 13.188 2.75 12.751 2.75 12C2.75 11.249 2.847 10.812 3.005 10.454C3.174 10.072 3.432 9.732 3.87 9.162Z"
                                      fill="#155DFC"
                                    />
                                  </svg>
                                </button>
                              </Link>

                              {PRINTABLE_STATUSES.includes(
                                item.status ?? item.raw?.status,
                              ) && (
                                <button
                                  className={styles["apt-view-btn"]}
                                  title="Print Invoice"
                                  onClick={() => openInvoice(item)}
                                  style={{
                                    background: "none",
                                    border: "none",
                                    cursor: "pointer",
                                  }}
                                >
                                  <FiPrinter size={18} color="#111827" />
                                </button>
                              )}

                              <ChatIconWithBadge
                                className={styles["apt-view-btn"]}
                                title="Chat"
                                count={unreadForAppointment(
                                  item.raw?.id ??
                                    item.raw?.booking_id ??
                                    item.id,
                                )}
                                onClick={() => openChat(item)}
                              />
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            )}

            {/* Scheduled tab  */}
            {activeTab === "Scheduled" && (
              <div className={styles["apt-appointments-table"]}>
                <table>
                  <thead>
                    <tr>
                      <th>{t("lab.orderManagement.table.id")}</th>
                      <th>{t("lab.orderManagement.table.name")}</th>
                      {/* Child bookings treat the CHILD as the patient, so the child gets its own
                          column rather than being conflated with the account holder. */}
                      <th>{t("childBooking.childName", "Child Name")}</th>
                      <th>{t("lab.orderManagement.table.testName")}</th>
                      <th>{t("lab.orderManagement.table.serviceDate")}</th>
                      <th>{t("lab.orderManagement.table.serviceTime")}</th>
                      <th>{t("lab.orderManagement.table.testType")}</th>
                      <th>{t("lab.orderManagement.table.urgency")}</th>
                      <th>{t("lab.orderManagement.table.technician")}</th>
                      <th>{t("lab.orderManagement.table.location")}</th>
                      <th>{t("lab.orderManagement.table.totalPrice")}</th>
                      <th>{t("lab.orderManagement.table.paymentStatus")}</th>
                      <th>{t("lab.orderManagement.table.action")}</th>
                    </tr>
                  </thead>

                  <tbody>
                    {loading ? (
                      <tr>
                        <td colSpan={13}>
                          <Loader />
                        </td>
                      </tr>
                    ) : sortedAppointments.filter(
                        (item) => item.status === "TECHNICIAN_ASSIGNED",
                      ).length === 0 ? (
                      <tr>
                        <td
                          colSpan={13}
                          style={{ textAlign: "center", padding: "20px" }}
                        >
                          {t("lab.orderManagement.noData.scheduled")}
                        </td>
                      </tr>
                    ) : (
                      sortedAppointments
                        .filter((item) => item.status === "TECHNICIAN_ASSIGNED")
                        .map((item, idx) => (
                          <tr key={idx}>
                            <td>{item.bookingNumber || item.id}</td>
                            <td>
                              <BookingPatientNameCell
                                childName={item.childName}
                                name={item.guardianName}
                              />
                            </td>
                            <td>
                              <BookingChildNameCell childName={item.childName} />
                            </td>

                            <td>
                              {item.testName && item.testName.length > 20 ? (
                                <div className={styles["testNames"]}>
                                  <div className={styles["truncateTestNames"]}>
                                    {item.testName}
                                  </div>
                                  <div className={styles["testNamesList"]}>
                                    <ul>
                                      <li>
                                        <span>
                                          {t(
                                            "lab.orderManagement.testNames.tests",
                                          )}
                                        </span>
                                      </li>
                                      {item.testName
                                        .split(",")
                                        .map((test, index) => (
                                          <li key={index}>{test.trim()}</li>
                                        ))}
                                    </ul>
                                  </div>
                                </div>
                              ) : (
                                item.testName || "N/A"
                              )}
                            </td>

                            <td>{item.serviceDate || "N/A"}</td>
                            <td>{item.serviceTime || "N/A"}</td>

                            <td>
                              <span
                                className={`${styles["db-status"]} ${styles[getStatusColor(item.testType)]}`}
                              >
                                {item.testType}
                              </span>
                            </td>

                            <td>
                              {item.urgency ? (
                                <span
                                  className={`${styles["db-status"]} ${styles[getStatusColor(item.urgency)]}`}
                                >
                                  {item.urgency}
                                </span>
                              ) : (
                                <span
                                  className={`${styles["db-status"]} ${styles["status-noturgent"]}`}
                                >
                                  Not Urgent
                                </span>
                              )}
                            </td>

                            <td>{item.technician || ""}</td>

                            <td>{formatLocation(item.location)}</td>

                            <td>IQD {formatAmount(item.totalPrice)}</td>

                            {/* Payment Status */}
                            <td>
                              {item.paymentStatus ? (
                                <span
                                  className={`${styles["db-status"]} ${styles[getStatusColor(formatPaymentStatus(item.paymentStatus))]}`}
                                >
                                  {formatPaymentStatus(item.paymentStatus)}
                                </span>
                              ) : (
                                "N/A"
                              )}
                            </td>

                            {/* ACTION COLUMN (UNCHANGED) */}
                            <td>
                              <div className={styles["apt-action-buttons"]}>
                                <Link
                                  to={`/lab_appointment_detail/${item.id}?source=ordermanagement`}
                                  className={styles["appointment-link"]}
                                >
                                  <button
                                    className={styles["apt-view-btn"]}
                                    title="View"
                                  >
                                    <svg
                                      width="24"
                                      height="24"
                                      viewBox="0 0 24 24"
                                      fill="none"
                                      xmlns="http://www.w3.org/2000/svg"
                                    >
                                      <path
                                        fillRule="evenodd"
                                        clipRule="evenodd"
                                        d="M12 8.25C11.0054 8.25 10.0516 8.64509 9.34835 9.34835C8.64509 10.0516 8.25 11.0054 8.25 12C8.25 12.9946 8.64509 13.9484 9.34835 14.6516C10.0516 15.3549 11.0054 15.75 12 15.75C12.9946 15.75 13.9484 15.3549 14.6516 14.6516C15.3549 13.9484 15.75 12.9946 15.75 12C15.75 11.0054 15.3549 10.0516 14.6516 9.34835C13.9484 8.64509 12.9946 8.25 12 8.25ZM9.75 12C9.75 11.4033 9.98705 10.831 10.409 10.409C10.831 9.98705 11.4033 9.75 12 9.75C12.5967 9.75 13.169 9.98705 13.591 10.409C14.0129 10.831 14.25 11.4033 14.25 12C14.25 12.5967 14.0129 13.169 13.591 13.591C13.169 14.0129 12.5967 14.25 12 14.25C11.4033 14.25 10.831 14.0129 10.409 13.591C9.98705 13.169 9.75 12.5967 9.75 12Z"
                                        fill="#155DFC"
                                      />
                                      <path
                                        fillRule="evenodd"
                                        clipRule="evenodd"
                                        d="M12 3.25C7.486 3.25 4.445 5.954 2.68 8.247L2.649 8.288C2.249 8.807 1.882 9.284 1.633 9.848C1.366 10.453 1.25 11.112 1.25 12C1.25 12.888 1.366 13.547 1.633 14.152C1.883 14.716 2.25 15.194 2.649 15.712L2.681 15.753C4.445 18.046 7.486 20.75 12 20.75C16.514 20.75 19.555 18.046 21.32 15.753L21.351 15.712C21.751 15.194 22.118 14.716 22.367 14.152C22.634 13.547 22.75 12.888 22.75 12C22.75 11.112 22.634 10.453 22.367 9.848C22.117 9.284 21.75 8.807 21.351 8.288L21.319 8.247C19.555 5.954 16.514 3.25 12 3.25ZM3.87 9.162C5.498 7.045 8.15 4.75 12 4.75C15.85 4.75 18.501 7.045 20.13 9.162C20.57 9.732 20.826 10.072 20.995 10.454C21.153 10.812 21.25 11.249 21.25 12C21.25 12.751 21.153 13.188 20.995 13.546C20.826 13.928 20.569 14.268 20.131 14.838C18.5 16.955 15.85 19.25 12 19.25C8.15 19.25 5.499 16.955 3.87 14.838C3.43 14.268 3.174 13.928 3.005 13.546C2.847 13.188 2.75 12.751 2.75 12C2.75 11.249 2.847 10.812 3.005 10.454C3.174 10.072 3.432 9.732 3.87 9.162Z"
                                        fill="#155DFC"
                                      />
                                    </svg>
                                  </button>
                                </Link>

                                {PRINTABLE_STATUSES.includes(
                                  item.status ?? item.raw?.status,
                                ) && (
                                  <button
                                    className={styles["apt-view-btn"]}
                                    title="Print Invoice"
                                    onClick={() => openInvoice(item)}
                                    style={{
                                      background: "none",
                                      border: "none",
                                      cursor: "pointer",
                                    }}
                                  >
                                    <FiPrinter size={18} color="#111827" />
                                  </button>
                                )}

                                <ChatIconWithBadge
                                  className={styles["apt-view-btn"]}
                                  title="Chat"
                                  count={unreadForAppointment(
                                    item.raw?.id ??
                                      item.raw?.booking_id ??
                                      item.id,
                                  )}
                                  onClick={() => openChat(item)}
                                />
                              </div>
                            </td>
                          </tr>
                        ))
                    )}
                  </tbody>
                </table>
              </div>
            )}

            {/* Dispatched tab  */}
            {activeTab === "Dispatched" && (
              <div className={styles["apt-appointments-table"]}>
                <table>
                  <thead>
                    <tr>
                      <th>{t("lab.orderManagement.table.id")}</th>
                      <th>{t("lab.orderManagement.table.name")}</th>
                      {/* Child bookings treat the CHILD as the patient, so the child gets its own
                          column rather than being conflated with the account holder. */}
                      <th>{t("childBooking.childName", "Child Name")}</th>
                      <th>{t("lab.orderManagement.table.testName")}</th>
                      <th>{t("lab.orderManagement.table.serviceDate")}</th>
                      <th>{t("lab.orderManagement.table.serviceTime")}</th>
                      <th>{t("lab.orderManagement.table.testType")}</th>
                      <th>{t("lab.orderManagement.table.urgency")}</th>
                      <th>{t("lab.orderManagement.table.technician")}</th>
                      <th>{t("lab.orderManagement.table.location")}</th>
                      <th>{t("lab.orderManagement.table.totalPrice")}</th>
                      <th>{t("lab.orderManagement.table.paymentStatus")}</th>
                      <th>{t("lab.orderManagement.table.action")}</th>
                    </tr>
                  </thead>

                  <tbody>
                    {loading ? (
                      <tr>
                        <td colSpan={13}>
                          <Loader />
                        </td>
                      </tr>
                    ) : sortedAppointments.filter(
                        (item) => item.status === "TECHNICIAN_DISPATCHED",
                      ).length === 0 ? (
                      <tr>
                        <td
                          colSpan={13}
                          style={{ textAlign: "center", padding: "20px" }}
                        >
                          {t(
                            "lab.orderManagement.noData.dispatched",
                            "No dispatched appointments found",
                          )}
                        </td>
                      </tr>
                    ) : (
                      sortedAppointments
                        .filter(
                          (item) => item.status === "TECHNICIAN_DISPATCHED",
                        )
                        .map((item, idx) => (
                          <tr key={idx}>
                            <td>{item.bookingNumber || item.id}</td>
                            <td>
                              <BookingPatientNameCell
                                childName={item.childName}
                                name={item.guardianName}
                              />
                            </td>
                            <td>
                              <BookingChildNameCell childName={item.childName} />
                            </td>

                            <td>
                              {item.testName && item.testName.length > 20 ? (
                                <div className={styles["testNames"]}>
                                  <div className={styles["truncateTestNames"]}>
                                    {item.testName}
                                  </div>
                                  <div className={styles["testNamesList"]}>
                                    <ul>
                                      <li>
                                        <span>
                                          {t(
                                            "lab.orderManagement.testNames.tests",
                                          )}
                                        </span>
                                      </li>
                                      {item.testName
                                        .split(",")
                                        .map((test, index) => (
                                          <li key={index}>{test.trim()}</li>
                                        ))}
                                    </ul>
                                  </div>
                                </div>
                              ) : (
                                item.testName || "N/A"
                              )}
                            </td>

                            <td>{item.serviceDate || "N/A"}</td>
                            <td>{item.serviceTime || "N/A"}</td>

                            <td>
                              <span
                                className={`${styles["db-status"]} ${styles[getStatusColor(item.testType)]}`}
                              >
                                {item.testType}
                              </span>
                            </td>

                            <td>
                              {item.urgency ? (
                                <span
                                  className={`${styles["db-status"]} ${styles[getStatusColor(item.urgency)]}`}
                                >
                                  {item.urgency}
                                </span>
                              ) : (
                                <span
                                  className={`${styles["db-status"]} ${styles["status-noturgent"]}`}
                                >
                                  Not Urgent
                                </span>
                              )}
                            </td>

                            <td>{item.technician || ""}</td>

                            <td>{formatLocation(item.location)}</td>

                            <td>IQD {formatAmount(item.totalPrice)}</td>

                            {/* Payment Status */}
                            <td>
                              {item.paymentStatus ? (
                                <span
                                  className={`${styles["db-status"]} ${styles[getStatusColor(formatPaymentStatus(item.paymentStatus))]}`}
                                >
                                  {formatPaymentStatus(item.paymentStatus)}
                                </span>
                              ) : (
                                "N/A"
                              )}
                            </td>

                            {/* ACTION COLUMN */}
                            <td>
                              <div className={styles["apt-action-buttons"]}>
                                <Link
                                  to={`/lab_appointment_detail/${item.id}?source=ordermanagement`}
                                  className={styles["appointment-link"]}
                                >
                                  <button
                                    className={styles["apt-view-btn"]}
                                    title="View"
                                  >
                                    <svg
                                      width="24"
                                      height="24"
                                      viewBox="0 0 24 24"
                                      fill="none"
                                      xmlns="http://www.w3.org/2000/svg"
                                    >
                                      <path
                                        fillRule="evenodd"
                                        clipRule="evenodd"
                                        d="M12 8.25C11.0054 8.25 10.0516 8.64509 9.34835 9.34835C8.64509 10.0516 8.25 11.0054 8.25 12C8.25 12.9946 8.64509 13.9484 9.34835 14.6516C10.0516 15.3549 11.0054 15.75 12 15.75C12.9946 15.75 13.9484 15.3549 14.6516 14.6516C15.3549 13.9484 15.75 12.9946 15.75 12C15.75 11.0054 15.3549 10.0516 14.6516 9.34835C13.9484 8.64509 12.9946 8.25 12 8.25ZM9.75 12C9.75 11.4033 9.98705 10.831 10.409 10.409C10.831 9.98705 11.4033 9.75 12 9.75C12.5967 9.75 13.169 9.98705 13.591 10.409C14.0129 10.831 14.25 11.4033 14.25 12C14.25 12.5967 14.0129 13.169 13.591 13.591C13.169 14.0129 12.5967 14.25 12 14.25C11.4033 14.25 10.831 14.0129 10.409 13.591C9.98705 13.169 9.75 12.5967 9.75 12Z"
                                        fill="#155DFC"
                                      />
                                      <path
                                        fillRule="evenodd"
                                        clipRule="evenodd"
                                        d="M12 3.25C7.486 3.25 4.445 5.954 2.68 8.247L2.649 8.288C2.249 8.807 1.882 9.284 1.633 9.848C1.366 10.453 1.25 11.112 1.25 12C1.25 12.888 1.366 13.547 1.633 14.152C1.883 14.716 2.25 15.194 2.649 15.712L2.681 15.753C4.445 18.046 7.486 20.75 12 20.75C16.514 20.75 19.555 18.046 21.32 15.753L21.351 15.712C21.751 15.194 22.118 14.716 22.367 14.152C22.634 13.547 22.75 12.888 22.75 12C22.75 11.112 22.634 10.453 22.367 9.848C22.117 9.284 21.75 8.807 21.351 8.288L21.319 8.247C19.555 5.954 16.514 3.25 12 3.25ZM3.87 9.162C5.498 7.045 8.15 4.75 12 4.75C15.85 4.75 18.501 7.045 20.13 9.162C20.57 9.732 20.826 10.072 20.995 10.454C21.153 10.812 21.25 11.249 21.25 12C21.25 12.751 21.153 13.188 20.995 13.546C20.826 13.928 20.569 14.268 20.131 14.838C18.5 16.955 15.85 19.25 12 19.25C8.15 19.25 5.499 16.955 3.87 14.838C3.43 14.268 3.174 13.928 3.005 13.546C2.847 13.188 2.75 12.751 2.75 12C2.75 11.249 2.847 10.812 3.005 10.454C3.174 10.072 3.432 9.732 3.87 9.162Z"
                                        fill="#155DFC"
                                      />
                                    </svg>
                                  </button>
                                </Link>

                                {PRINTABLE_STATUSES.includes(
                                  item.status ?? item.raw?.status,
                                ) && (
                                  <button
                                    className={styles["apt-view-btn"]}
                                    title="Print Invoice"
                                    onClick={() => openInvoice(item)}
                                    style={{
                                      background: "none",
                                      border: "none",
                                      cursor: "pointer",
                                    }}
                                  >
                                    <FiPrinter size={18} color="#111827" />
                                  </button>
                                )}

                                <ChatIconWithBadge
                                  className={styles["apt-view-btn"]}
                                  title="Chat"
                                  count={unreadForAppointment(
                                    item.raw?.id ??
                                      item.raw?.booking_id ??
                                      item.id,
                                  )}
                                  onClick={() => openChat(item)}
                                />
                              </div>
                            </td>
                          </tr>
                        ))
                    )}
                  </tbody>
                </table>
              </div>
            )}

            {/* In-Progress tab  */}
            {activeTab === "In-Progress" && (
              <div className={styles["apt-appointments-table"]}>
                <table>
                  <thead>
                    <tr>
                      <th>{t("lab.orderManagement.table.id")}</th>
                      <th>{t("lab.orderManagement.table.name")}</th>
                      {/* Child bookings treat the CHILD as the patient, so the child gets its own
                          column rather than being conflated with the account holder. */}
                      <th>{t("childBooking.childName", "Child Name")}</th>
                      <th>{t("lab.orderManagement.table.testName")}</th>
                      <th>{t("lab.orderManagement.table.serviceDate")}</th>
                      <th>{t("lab.orderManagement.table.serviceTime")}</th>
                      <th>{t("lab.orderManagement.table.testType")}</th>
                      <th>{t("lab.orderManagement.table.urgency")}</th>
                      <th>{t("lab.orderManagement.table.location")}</th>
                      <th>{t("lab.orderManagement.table.sampleStatus")}</th>
                      <th>{t("lab.orderManagement.table.paymentStatus")}</th>
                      <th>{t("lab.orderManagement.table.technicianStatus")}</th>
                      <th>{t("lab.orderManagement.table.action")}</th>
                    </tr>
                  </thead>

                  <tbody>
                    {loading ? (
                      <tr>
                        <td colSpan={13}>
                          <Loader />
                        </td>
                      </tr>
                    ) : sortedAppointments.filter(
                        (item) => item.status === "IN_PROGRESS",
                      ).length === 0 ? (
                      <tr>
                        <td
                          colSpan={13}
                          style={{ textAlign: "center", padding: "20px" }}
                        >
                          {t("lab.orderManagement.noData.inProgress")}
                        </td>
                      </tr>
                    ) : (
                      sortedAppointments
                        .filter((item) => item.status === "IN_PROGRESS")
                        .map((item, idx) => (
                          <tr key={idx}>
                            <td>{item.bookingNumber || item.id}</td>
                            <td>
                              <BookingPatientNameCell
                                childName={item.childName}
                                name={item.guardianName}
                              />
                            </td>
                            <td>
                              <BookingChildNameCell childName={item.childName} />
                            </td>

                            <td>
                              {item.testName && item.testName.length > 20 ? (
                                <div className={styles["testNames"]}>
                                  <div className={styles["truncateTestNames"]}>
                                    {item.testName}
                                  </div>
                                  <div className={styles["testNamesList"]}>
                                    <ul>
                                      <li>
                                        <span>
                                          {t(
                                            "lab.orderManagement.testNames.tests",
                                          )}
                                        </span>
                                      </li>
                                      {item.testName
                                        .split(",")
                                        .map((test, index) => (
                                          <li key={index}>{test.trim()}</li>
                                        ))}
                                    </ul>
                                  </div>
                                </div>
                              ) : (
                                item.testName || "N/A"
                              )}
                            </td>

                            <td>{item.serviceDate || "N/A"}</td>
                            <td>{item.serviceTime || "N/A"}</td>

                            <td>
                              {item.testType ? (
                                <span
                                  className={`${styles["db-status"]} ${styles[getStatusColor(item.testType)]}`}
                                >
                                  {item.testType}
                                </span>
                              ) : (
                                "Lab"
                              )}
                            </td>

                            <td>
                              {item.urgency ? (
                                <span
                                  className={`${styles["db-status"]} ${styles[getStatusColor(item.urgency)]}`}
                                >
                                  {item.urgency}
                                </span>
                              ) : (
                                <span
                                  className={`${styles["db-status"]} ${styles["status-noturgent"]}`}
                                >
                                  Not Urgent
                                </span>
                              )}
                            </td>

                            <td>
                              {item.location ? (
                                <span
                                  className={`${styles["db-status"]} ${styles[getStatusColor(formatLocation(item.location))]}`}
                                >
                                  {formatLocation(item.location)}
                                </span>
                              ) : (
                                "N/A"
                              )}
                            </td>

                            <td>
                              {item.sampleStatus ? (
                                <span
                                  className={`${styles["db-status"]} ${styles[getStatusColor(item.sampleStatus)]}`}
                                >
                                  {item.sampleStatus}
                                </span>
                              ) : (
                                "Pending"
                              )}
                            </td>

                            {/* Payment Status */}
                            <td>
                              {item.paymentStatus ? (
                                <span
                                  className={`${styles["db-status"]} ${styles[getStatusColor(formatPaymentStatus(item.paymentStatus))]}`}
                                >
                                  {formatPaymentStatus(item.paymentStatus)}
                                </span>
                              ) : (
                                "Pending"
                              )}
                            </td>

                            {/* Technician Status */}
                            <td>
                              {(() => {
                                const techLabel =
                                  item.technicianStatus ||
                                  (item.status === "IN_PROGRESS"
                                    ? "In Progress"
                                    : item.status === "TECHNICIAN_DISPATCHED"
                                      ? "Technician Dispatched"
                                      : item.status === "TECHNICIAN_ASSIGNED"
                                        ? "Technician Assigned"
                                        : item.status === "COMPLETED"
                                          ? "Completed"
                                          : "N/A");
                                return (
                                  <span
                                    className={`${styles["db-status"]} ${styles[getStatusColor(techLabel)]}`}
                                  >
                                    {techLabel}
                                  </span>
                                );
                              })()}
                            </td>

                            {/* ACTION COLUMN (UNCHANGED) */}
                            <td>
                              <div className={styles["apt-action-buttons"]}>
                                <Link
                                  to={`/lab_appointment_detail/${item.id}?source=ordermanagement`}
                                >
                                  <button
                                    className={styles["apt-view-btn"]}
                                    title="View"
                                  >
                                    <svg
                                      width="24"
                                      height="24"
                                      viewBox="0 0 24 24"
                                      fill="none"
                                      xmlns="http://www.w3.org/2000/svg"
                                    >
                                      <path
                                        fillRule="evenodd"
                                        clipRule="evenodd"
                                        d="M12 8.25C11.0054 8.25 10.0516 8.64509 9.34835 9.34835C8.64509 10.0516 8.25 11.0054 8.25 12C8.25 12.9946 8.64509 13.9484 9.34835 14.6516C10.0516 15.3549 11.0054 15.75 12 15.75C12.9946 15.75 13.9484 15.3549 14.6516 14.6516C15.3549 13.9484 15.75 12.9946 15.75 12C15.75 11.0054 15.3549 10.0516 14.6516 9.34835C13.9484 8.64509 12.9946 8.25 12 8.25ZM9.75 12C9.75 11.4033 9.98705 10.831 10.409 10.409C10.831 9.98705 11.4033 9.75 12 9.75C12.5967 9.75 13.169 9.98705 13.591 10.409C14.0129 10.831 14.25 11.4033 14.25 12C14.25 12.5967 14.0129 13.169 13.591 13.591C13.169 14.0129 12.5967 14.25 12 14.25C11.4033 14.25 10.831 14.0129 10.409 13.591C9.98705 13.169 9.75 12.5967 9.75 12Z"
                                        fill="#155DFC"
                                      />
                                      <path
                                        fillRule="evenodd"
                                        clipRule="evenodd"
                                        d="M12 3.25C7.486 3.25 4.445 5.954 2.68 8.247L2.649 8.288C2.249 8.807 1.882 9.284 1.633 9.848C1.366 10.453 1.25 11.112 1.25 12C1.25 12.888 1.366 13.547 1.633 14.152C1.883 14.716 2.25 15.194 2.649 15.712L2.681 15.753C4.445 18.046 7.486 20.75 12 20.75C16.514 20.75 19.555 18.046 21.32 15.753L21.351 15.712C21.751 15.194 22.118 14.716 22.367 14.152C22.634 13.547 22.75 12.888 22.75 12C22.75 11.112 22.634 10.453 22.367 9.848C22.117 9.284 21.75 8.807 21.351 8.288L21.319 8.247C19.555 5.954 16.514 3.25 12 3.25ZM3.87 9.162C5.498 7.045 8.15 4.75 12 4.75C15.85 4.75 18.501 7.045 20.13 9.162C20.57 9.732 20.826 10.072 20.995 10.454C21.153 10.812 21.25 11.249 21.25 12C21.25 12.751 21.153 13.188 20.995 13.546C20.826 13.928 20.569 14.268 20.131 14.838C18.5 16.955 15.85 19.25 12 19.25C8.15 19.25 5.499 16.955 3.87 14.838C3.43 14.268 3.174 13.928 3.005 13.546C2.847 13.188 2.75 12.751 2.75 12C2.75 11.249 2.847 10.812 3.005 10.454C3.174 10.072 3.432 9.732 3.87 9.162Z"
                                        fill="#155DFC"
                                      />
                                    </svg>
                                  </button>
                                </Link>

                                {PRINTABLE_STATUSES.includes(
                                  item.status ?? item.raw?.status,
                                ) && (
                                  <button
                                    className={styles["apt-view-btn"]}
                                    title="Print Invoice"
                                    onClick={() => openInvoice(item)}
                                    style={{
                                      background: "none",
                                      border: "none",
                                      cursor: "pointer",
                                    }}
                                  >
                                    <FiPrinter size={18} color="#111827" />
                                  </button>
                                )}

                                <ChatIconWithBadge
                                  className={styles["apt-view-btn"]}
                                  title="Chat"
                                  count={unreadForAppointment(
                                    item.raw?.id ??
                                      item.raw?.booking_id ??
                                      item.id,
                                  )}
                                  onClick={() => openChat(item)}
                                />
                              </div>
                            </td>
                          </tr>
                        ))
                    )}
                  </tbody>
                </table>
              </div>
            )}

            {/* Completed Tab (also handles the "Results / Pending Results"
                tab — both are COMPLETED bookings server-side, only the
                latter also requires `payment_status = COMPLETED` via the
                `RESULTS_PENDING_OR_SENT` pseudo-status in `getLabOrders`).
                Row layout is identical so we reuse the single render block
                instead of duplicating the table. */}
            {(activeTab === "Completed" ||
              activeTab === "Results / Pending Results") && (
              <div className={styles["apt-appointments-table"]}>
                <table>
                  <thead>
                    <tr>
                      <th>{t("lab.orderManagement.table.id")}</th>
                      <th>{t("lab.orderManagement.table.name")}</th>
                      {/* Child bookings treat the CHILD as the patient, so the child gets its own
                          column rather than being conflated with the account holder. */}
                      <th>{t("childBooking.childName", "Child Name")}</th>
                      <th>{t("lab.orderManagement.table.testName")}</th>
                      <th>{t("lab.orderManagement.table.serviceDate")}</th>
                      <th>{t("lab.orderManagement.table.serviceTime")}</th>
                      <th>{t("lab.orderManagement.table.testType")}</th>
                      <th>{t("lab.orderManagement.table.urgency")}</th>
                      <th>{t("lab.orderManagement.table.technician")}</th>
                      <th>{t("lab.orderManagement.table.location")}</th>
                      <th>{t("lab.orderManagement.table.totalPrice")}</th>
                      <th>{t("lab.orderManagement.table.paymentStatus")}</th>
                      <th>{t("lab.orderManagement.table.reportStatus")}</th>
                      <th>{t("lab.orderManagement.table.action")}</th>
                    </tr>
                  </thead>

                  <tbody>
                    {loading ? (
                      <tr>
                        <td colSpan={14}>
                          <Loader />
                        </td>
                      </tr>
                    ) : appointments.length === 0 ? (
                      <tr>
                        <td
                          colSpan={14}
                          style={{ textAlign: "center", padding: "20px" }}
                        >
                          {t("lab.orderManagement.noData.completed")}
                        </td>
                      </tr>
                    ) : (
                      appointments.map((item, idx) => (
                        <tr key={idx}>
                          <td>
                            <Link
                              to={`/lab_appointment_detail/${item.id}?source=ordermanagement`}
                              className={styles["appointment-id-link"]}
                            >
                              {item.raw?.bookingNumber || item.id}
                            </Link>
                          </td>
                          <td>
                            <BookingPatientNameCell
                              childName={item.childName}
                              name={item.guardianName}
                            />
                          </td>
                          <td>
                            <BookingChildNameCell childName={item.childName} />
                          </td>

                          <td>
                            {item.raw?.testName &&
                            item.raw.testName.length > 20 ? (
                              <div className={styles["testNames"]}>
                                <div className={styles["truncateTestNames"]}>
                                  {item.raw.testName}
                                </div>
                                <div className={styles["testNamesList"]}>
                                  <ul>
                                    <li>
                                      <span>
                                        {t(
                                          "lab.orderManagement.testNames.tests",
                                        )}
                                      </span>
                                    </li>
                                    {item.raw.testName
                                      .split(",")
                                      .map((test, index) => (
                                        <li key={index}>{test.trim()}</li>
                                      ))}
                                  </ul>
                                </div>
                              </div>
                            ) : (
                              item.raw?.testName || "N/A"
                            )}
                          </td>

                          <td>{item.raw?.serviceDate || "N/A"}</td>
                          <td>{formatTime12h(item.raw?.serviceTime)}</td>

                          <td>
                            <span
                              className={`${styles["db-status"]} ${styles[getStatusColor(item.raw?.testType || "Lab")]}`}
                            >
                              {item.raw?.testType || "Lab"}
                            </span>
                          </td>

                          <td>
                            {item.raw?.urgency ? (
                              <span
                                className={`${styles["db-status"]} ${styles[getStatusColor(item.raw.urgency)]}`}
                              >
                                {item.raw.urgency}
                              </span>
                            ) : (
                              ""
                            )}
                          </td>

                          <td>
                            {item.raw?.technician?.name ||
                              item.technician ||
                              "Not Assigned"}
                          </td>

                          <td>
                            <span
                              className={`${styles["db-status"]} ${styles[getStatusColor(formatLocation(item.raw?.preferredLocation || "On-Site"))]}`}
                            >
                              {formatLocation(
                                item.raw?.preferredLocation || "On-Site",
                              )}
                            </span>
                          </td>

                          <td>
                            IQD{" "}
                            {formatAmount(item.raw?.totalPrice)}
                          </td>

                          {/* Payment Status */}
                          <td>
                            <span
                              className={`${styles["db-status"]} ${styles[getStatusColor(formatPaymentStatus(item.raw?.paymentStatus || item.paymentStatus || "COMPLETED"))]}`}
                            >
                              {formatPaymentStatus(
                                item.raw?.paymentStatus ||
                                  item.paymentStatus ||
                                  "COMPLETED",
                              )}
                            </span>
                          </td>

                          {/* Report Status */}
                          <td>
                            <span
                              className={`${styles["db-status"]} ${styles[getReportStatusColor(item.raw?.reportStatus)]}`}
                            >
                              {formatReportStatus(item.raw?.reportStatus)}
                            </span>
                          </td>

                          {/* ACTION COLUMN */}
                          <td>
                            <div className={styles["apt-action-buttons"]}>
                              <button
                                className={styles["apt-view-btn"]}
                                title="View"
                                onClick={() =>
                                  navigate(
                                    `/lab_appointment_detail/${item.id}?source=ordermanagement`,
                                  )
                                }
                              >
                                <svg
                                  width="24"
                                  height="24"
                                  viewBox="0 0 24 24"
                                  fill="none"
                                  xmlns="http://www.w3.org/2000/svg"
                                >
                                  <path
                                    fillRule="evenodd"
                                    clipRule="evenodd"
                                    d="M12 8.25C11.0054 8.25 10.0516 8.64509 9.34835 9.34835C8.64509 10.0516 8.25 11.0054 8.25 12C8.25 12.9946 8.64509 13.9484 9.34835 14.6516C10.0516 15.3549 11.0054 15.75 12 15.75C12.9946 15.75 13.9484 15.3549 14.6516 14.6516C15.3549 13.9484 15.75 12.9946 15.75 12C15.75 11.0054 15.3549 10.0516 14.6516 9.34835C13.9484 8.64509 12.9946 8.25 12 8.25ZM9.75 12C9.75 11.4033 9.98705 10.831 10.409 10.409C10.831 9.98705 11.4033 9.75 12 9.75C12.5967 9.75 13.169 9.98705 13.591 10.409C14.0129 10.831 14.25 11.4033 14.25 12C14.25 12.5967 14.0129 13.169 13.591 13.591C13.169 14.0129 12.5967 14.25 12 14.25C11.4033 14.25 10.831 14.0129 10.409 13.591C9.98705 13.169 9.75 12.5967 9.75 12Z"
                                    fill="#155DFC"
                                  />
                                  <path
                                    fillRule="evenodd"
                                    clipRule="evenodd"
                                    d="M12 3.25C7.486 3.25 4.445 5.954 2.68 8.247L2.649 8.288C2.249 8.807 1.882 9.284 1.633 9.848C1.366 10.453 1.25 11.112 1.25 12C1.25 12.888 1.366 13.547 1.633 14.152C1.883 14.716 2.25 15.194 2.649 15.712L2.681 15.753C4.445 18.046 7.486 20.75 12 20.75C16.514 20.75 19.555 18.046 21.32 15.753L21.351 15.712C21.751 15.194 22.118 14.716 22.367 14.152C22.634 13.547 22.75 12.888 22.75 12C22.75 11.112 22.634 10.453 22.367 9.848C22.117 9.284 21.75 8.807 21.351 8.288L21.319 8.247C19.555 5.954 16.514 3.25 12 3.25ZM3.87 9.162C5.498 7.045 8.15 4.75 12 4.75C15.85 4.75 18.501 7.045 20.13 9.162C20.57 9.732 20.826 10.072 20.995 10.454C21.153 10.812 21.25 11.249 21.25 12C21.25 12.751 21.153 13.188 20.995 13.546C20.826 13.928 20.569 14.268 20.131 14.838C18.5 16.955 15.85 19.25 12 19.25C8.15 19.25 5.499 16.955 3.87 14.838C3.43 14.268 3.174 13.928 3.005 13.546C2.847 13.188 2.75 12.751 2.75 12C2.75 11.249 2.847 10.812 3.005 10.454C3.174 10.072 3.432 9.732 3.87 9.162Z"
                                    fill="#155DFC"
                                  />
                                </svg>
                              </button>

                              {PRINTABLE_STATUSES.includes(
                                item.status ?? item.raw?.status,
                              ) && (
                                <button
                                  className={styles["apt-view-btn"]}
                                  title="Print Invoice"
                                  onClick={() => openInvoice(item)}
                                  style={{
                                    background: "none",
                                    border: "none",
                                    cursor: "pointer",
                                  }}
                                >
                                  <FiPrinter size={18} color="#111827" />
                                </button>
                              )}

                              <ChatIconWithBadge
                                className={styles["apt-view-btn"]}
                                title="Chat"
                                count={unreadForAppointment(
                                  item.raw?.id ??
                                    item.raw?.booking_id ??
                                    item.id,
                                )}
                                onClick={() => openChat(item)}
                              />
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            )}

            {/* Rejected Tab */}
            {activeTab === "Rejected" && (
              <div className={styles["apt-appointments-table"]}>
                <table>
                  <thead>
                    <tr>
                      <th>{t("lab.orderManagement.table.id")}</th>
                      <th>{t("lab.orderManagement.table.name")}</th>
                      {/* Child bookings treat the CHILD as the patient, so the child gets its own
                          column rather than being conflated with the account holder. */}
                      <th>{t("childBooking.childName", "Child Name")}</th>
                      <th>{t("lab.orderManagement.table.testName")}</th>
                      {renderSortableHeader(
                        "requestDateRaw",
                        t("lab.orderManagement.table.requestDate"),
                      )}
                      <th>{t("lab.orderManagement.table.preferredDate")}</th>
                      <th>
                        {t("lab.orderManagement.table.preferredTimeSlot")}
                      </th>
                      <th>{t("lab.orderManagement.table.testType")}</th>
                      <th>{t("lab.orderManagement.table.urgency")}</th>
                      <th>{t("lab.orderManagement.table.preferredGender")}</th>
                      <th>
                        {t("lab.orderManagement.table.preferredLocation")}
                      </th>
                      <th>{t("lab.orderManagement.table.action")}</th>
                    </tr>
                  </thead>

                  <tbody>
                    {loading ? (
                      <tr>
                        <td colSpan={12}>
                          <Loader />
                        </td>
                      </tr>
                    ) : paginatedAppointments.length === 0 ? (
                      <tr>
                        <td
                          colSpan={12}
                          style={{ textAlign: "center", padding: "20px" }}
                        >
                          {t("lab.orderManagement.noData.rejected")}
                        </td>
                      </tr>
                    ) : (
                      paginatedAppointments.map((item, idx) => (
                        <tr key={idx}>
                          <td>{item.raw?.bookingNumber || item.id}</td>
                          <td>
                            <BookingPatientNameCell
                              childName={item.childName}
                              name={item.guardianName}
                            />
                          </td>
                          <td>
                            <BookingChildNameCell childName={item.childName} />
                          </td>

                          <td>
                            {item.raw?.testName &&
                            item.raw.testName.length > 20 ? (
                              <div className={styles["testNames"]}>
                                <div className={styles["truncateTestNames"]}>
                                  {item.raw.testName}
                                </div>
                                <div className={styles["testNamesList"]}>
                                  <ul>
                                    <li>
                                      <span>
                                        {t(
                                          "lab.orderManagement.testNames.tests",
                                        )}
                                      </span>
                                    </li>
                                    {item.raw.testName
                                      .split(",")
                                      .map((test, index) => (
                                        <li key={index}>{test.trim()}</li>
                                      ))}
                                  </ul>
                                </div>
                              </div>
                            ) : (
                              item.raw?.testName || "N/A"
                            )}
                          </td>

                          <td>
                            {item.requestDate || item.raw?.serviceDate || "N/A"}
                          </td>
                          {/* Patient's preferred date / slot (what the headers say), same as the
                              Received tab. serviceDate/serviceTime are only set once the order is
                              scheduled, so orders rejected before scheduling showed N/A here. */}
                          <td>{item.preferredDate || "N/A"}</td>
                          <td>{item.preferredTimeSlot || "N/A"}</td>

                          <td>
                            <span
                              className={`${styles["db-status"]} ${styles[getStatusColor(item.raw?.testType || "Lab")]}`}
                            >
                              {item.raw?.testType || "Lab"}
                            </span>
                          </td>

                          <td>
                            {item.raw?.urgency ? (
                              <span
                                className={`${styles["db-status"]} ${styles[getStatusColor(item.raw.urgency)]}`}
                              >
                                {item.raw.urgency}
                              </span>
                            ) : (
                              ""
                            )}
                          </td>

                          <td>{item.raw?.preferredGender || ""}</td>
                          <td>
                            <span
                              className={`${styles["db-status"]} ${styles[getStatusColor(formatLocation(item.raw?.preferredLocation || "On-Site"))]}`}
                            >
                              {formatLocation(
                                item.raw?.preferredLocation || "On-Site",
                              )}
                            </span>
                          </td>

                          {/* ACTION COLUMN (UNCHANGED) */}
                          <td>
                            <div className={styles["apt-action-buttons"]}>
                              <Link
                                to={`/lab_appointment_detail/${item.id}?source=ordermanagement`}
                              >
                                <button
                                  className={styles["apt-view-btn"]}
                                  title="View"
                                >
                                  <svg
                                    width="24"
                                    height="24"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                  >
                                    <path
                                      fillRule="evenodd"
                                      clipRule="evenodd"
                                      d="M12 8.25C11.0054 8.25 10.0516 8.64509 9.34835 9.34835C8.64509 10.0516 8.25 11.0054 8.25 12C8.25 12.9946 8.64509 13.9484 9.34835 14.6516C10.0516 15.3549 11.0054 15.75 12 15.75C12.9946 15.75 13.9484 15.3549 14.6516 14.6516C15.3549 13.9484 15.75 12.9946 15.75 12C15.75 11.0054 15.3549 10.0516 14.6516 9.34835C13.9484 8.64509 12.9946 8.25 12 8.25ZM9.75 12C9.75 11.4033 9.98705 10.831 10.409 10.409C10.831 9.98705 11.4033 9.75 12 9.75C12.5967 9.75 13.169 9.98705 13.591 10.409C14.0129 10.831 14.25 11.4033 14.25 12C14.25 12.5967 14.0129 13.169 13.591 13.591C13.169 14.0129 12.5967 14.25 12 14.25C11.4033 14.25 10.831 14.0129 10.409 13.591C9.98705 13.169 9.75 12.5967 9.75 12Z"
                                      fill="#155DFC"
                                    />
                                    <path
                                      fillRule="evenodd"
                                      clipRule="evenodd"
                                      d="M12 3.25C7.486 3.25 4.445 5.954 2.68 8.247L2.649 8.288C2.249 8.807 1.882 9.284 1.633 9.848C1.366 10.453 1.25 11.112 1.25 12C1.25 12.888 1.366 13.547 1.633 14.152C1.883 14.716 2.25 15.194 2.649 15.712L2.681 15.753C4.445 18.046 7.486 20.75 12 20.75C16.514 20.75 19.555 18.046 21.32 15.753L21.351 15.712C21.751 15.194 22.118 14.716 22.367 14.152C22.634 13.547 22.75 12.888 22.75 12C22.75 11.112 22.634 10.453 22.367 9.848C22.117 9.284 21.75 8.807 21.351 8.288L21.319 8.247C19.555 5.954 16.514 3.25 12 3.25ZM3.87 9.162C5.498 7.045 8.15 4.75 12 4.75C15.85 4.75 18.501 7.045 20.13 9.162C20.57 9.732 20.826 10.072 20.995 10.454C21.153 10.812 21.25 11.249 21.25 12C21.25 12.751 21.153 13.188 20.995 13.546C20.826 13.928 20.569 14.268 20.131 14.838C18.5 16.955 15.85 19.25 12 19.25C8.15 19.25 5.499 16.955 3.87 14.838C3.43 14.268 3.174 13.928 3.005 13.546C2.847 13.188 2.75 12.751 2.75 12C2.75 11.249 2.847 10.812 3.005 10.454C3.174 10.072 3.432 9.732 3.87 9.162Z"
                                      fill="#155DFC"
                                    />
                                  </svg>
                                </button>
                              </Link>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            )}

            {/* Pagination */}
            <div className={styles["apt-pagination"]}>
              <div className={styles["apt-pagination-info"]}>
                {t("lab.orderManagement.pagination.showing")}{" "}
                {(currentPage - 1) * itemsPerPage + 1}{" "}
                {t("lab.orderManagement.pagination.to")}{" "}
                {(currentPage - 1) * itemsPerPage +
                  paginatedAppointments.length}{" "}
                {t("lab.orderManagement.pagination.of")}{" "}
                {totalPages * itemsPerPage}
              </div>
              <div className={styles["apt-pagination-controls"]}>
                <button
                  className={`${styles["apt-pagination-btn"]} ${
                    currentPage === 1 ? styles["disabled"] : ""
                  }`}
                  // onClick={() => handlePageChange(currentPage - 1)}
                  // onClick={() => handlePageChange11(currentPage)}
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                >
                  {t("lab.orderManagement.pagination.previous")}
                </button>
                <div className={styles["apt-page-numbers"]}>
                  {/* {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    const page = i + 1;
                    return (
                      <button
                        key={page}
                        className={`${styles["apt-page-btn"]} ${
                          styles[`${currentPage === page ? "active" : ""}`]
                        }`}
                        onClick={() => handlePageChange(currentPage)}
                      >
                        {page}
                      </button>
                    );
                  })} */}

                  {Array.from({ length: totalPages }, (_, i) => {
                    const page = i + 1;
                    return (
                      <button
                        key={page}
                        className={`${styles["apt-page-btn"]} ${
                          styles[`${currentPage === page ? "active" : ""}`]
                        }`}
                        onClick={() => handlePageChange(page)}
                      >
                        {page}
                      </button>
                    );
                  })}
                </div>
                <button
                  className={`${styles["apt-pagination-btn"]} ${
                    styles["apt-next-btn"]
                  } ${currentPage === totalPages ? styles["disabled"] : ""}`}
                  // onClick={() => handlePageChange(currentPage + 1)}
                  // onClick={() => handlePageChange1(currentPage)}
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                >
                  {t("lab.orderManagement.pagination.next")}
                </button>
              </div>
            </div>
          </div>{" "}
        </>
      )}

      {isChatOpen && (
        <Chat
          onClose={closeChat}
          patientUserId={
            chatTarget?.raw?.patient_id ||
            chatTarget?.raw?.patient?.user_id ||
            chatTarget?.raw?.patient?.id
          }
          bookingId={chatTarget?.raw?.id || chatTarget?.raw?.booking_id}
          title={chatTarget?.name || "Chat"}
          subtitle={
            chatTarget?.bookingNumber || chatTarget?.raw?.booking_number || ""
          }
          avatar={chatTarget?.avatar}
        />
      )}

      {/* Shared invoice modal — identical layout + fee math everywhere. */}
      {showInvoiceModal && invoiceOrder && (
        <LabInvoiceModal
          open={showInvoiceModal}
          onClose={() => setShowInvoiceModal(false)}
          order={invoiceOrder}
          centerProfile={centerProfile}
        />
      )}
    </LabDashboardLayout>
  );
};

export default LabOrderManagement;
